"use client";

/**
 * EditorClient.tsx
 *
 * MVP editor bootstrap:
 * - Reads ?project=<project_public_id>&session=<token> for resume mode
 * - Loads editor-config via Next proxy (/api/print/editor-config)
 * - Creates a new project when no project param is provided (dev-safe against Strict Mode)
 * - Loads content + cover drafts
 * - Initializes default content/cover documents when drafts are missing
 * - Autosaves drafts every 15 seconds
 * - Provides a minimal "Add text" button for testing persistence
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useLocale, useTranslations } from "next-intl";
import { useEditorStore } from "./editorStore";
import CanvasEditor, { preloadCanvasImageUrls } from "./CanvasEditor";
import ContentBookSpreadsCanvas from "./ContentBookSpreadsCanvas";
import LocaleSwitcher from "./LocaleSwitcher";
import SaveAsDialog from "./SaveAsDialog";
import PropertiesDrawer from "./PropertiesDrawer";
import RightToolsPanel from "./RightToolsPanel";
import type { ClipartItem } from "./ClipartLibraryPanel";
import RightPagesPanel from "./RightPagesPanel";
import MobileBottomSheet from "./MobileBottomSheet";
import MobileEditorToolbar from "./MobileEditorToolbar";
import HelpChatModal from "./HelpChatModal";
import EditorOnboardingGuide from "./EditorOnboardingGuide";
import ShareInvitesModal from "./ShareInvitesModal";
import UnitPagination from "./UnitPagination";
import FontPicker, { type FontItem } from "./FontPicker";
import AccessGate from "@/app/ui/AccessGate";
import type { HelpAction } from "../help/faq";
import { THEME_TEMPLATES, getSpreadTemplates } from "../templates";
import { applyThemeTemplate, insertSpreadTemplateAtEnd } from "../templates/apply";
import { buildGeneratedThemeTemplate } from "../templates/generated";
import type { FormatGroup, ThemeTemplate } from "../templates/types";
import { getBackgroundPatternById } from "../backgrounds/patternLibrary";
import { inferCoverGeometryType, resolveCoverSpreadGeometry, type CoverGeometryType } from "@/lib/coverGeometry";
import { calculateSpineMm, type CoverThicknessType, type PaperBulkType } from "@/lib/spine";
import {
  createDefaultContentDataFromProduct,
  getMarginBoxesMm,
  normalizePageMarginsMm,
  ensureMinPhysicalPages,
  migratePagesToBookSpreadsUnits,
  normalizeContentLayout,
  physicalPageCountFromContentDoc,
} from "./contentUnits";
import type { ShapeId } from "./shapePresets";
import {
  buildPrintApiHeaders,
  getEditorAuthToken,
  getProjectClaimToken,
  issueProjectSession,
  persistProjectClaimToken,
  printApiDelete,
  printApiGet,
  printApiPost,
  printApiPut
} from "./printApi";

const EDITOR_ONBOARDING_STORAGE_KEY = "printapp_editor_onboarding_v1";

const I = {
  reset: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
      <path d="M3 12a9 9 0 1 0 3-6.7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M3 4v5h5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  ),
  center: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
      <path d="M12 3v4M12 17v4M3 12h4M17 12h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <circle cx="12" cy="12" r="2" stroke="currentColor" strokeWidth="2" />
    </svg>
  )
};

function IconLabelBtn({
  icon,
  label,
  title,
  onClick,
  disabled
}: {
  icon: ReactNode;
  label: string;
  title: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      className="editor-secondary-button gap-2 px-3 py-2 text-sm disabled:opacity-50"
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={title}
    >
      <span className="text-current">{icon}</span>
      <span className="whitespace-nowrap text-xs text-current">{label}</span>
    </button>
  );
}

type EditorConfig = {
  fonts: any[];
  frames: any[];
  formats: Array<{ id: string; page_mm: { width: number; height: number }; bleed_mm: number; safe_mm: number }>;
  defaults: any;
};

type Project = {
  id: number;
  public_id: string;
  name: string | null;
  created_at: string;
  claim_token?: string | null;
};

type ProjectSession = {
  project_id: number;
  user_id: string;
  role: string;
  expires_at: string;
  display_name?: string;
};

type InviteSession = {
  ok: boolean;
  token: string;
  expires_at: string;
  project_id: number;
  role: "guest_viewer" | "guest_editor";
};

type PresenceRow = {
  role: string;
  label: string;
  last_seen: string;
};

type ProjectLockState = {
  locked: boolean;
  by?: string;
  mine?: boolean;
  expires_at?: string;
};

// English comment: Deduplicate presence labels to avoid repeating "Guest (editor)" entries.
function uniqPresenceByLabel(rows: PresenceRow[]) {
  const seen = new Set<string>();
  const out: PresenceRow[] = [];
  for (const row of rows) {
    const key = String(row?.label || "");
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(row);
  }
  return out;
}

type DraftRow = {
  id: number;
  public_id?: string;
  kind: string;
  created_at: string;
  warnings_json?: any;
  data_json?: any;
};

type AssetRow = {
  id: number;
  project_id: number | null;
  original_name: string;
  mime_type: string;
  s3_key: string;
  width_px: number | null;
  height_px: number | null;
  thumbnail_s3_key?: string | null;
  thumbnail_mime_type?: string | null;
  thumbnail_width_px?: number | null;
  thumbnail_height_px?: number | null;
  created_at: string;
};

type AssetLibraryItem = AssetRow & {
  download_url?: string | null;
  thumbnail_url?: string | null;
};

type ProductConfig = {
  id: number;
  sku: string;
  title: string;
  title_i18n?: Record<string, string | undefined> | null;
  format_key: string;
  cover_mode: "spread" | "separate";
  content_layout: "book" | "spreads_only";
  output_mode?: string;
  min_pages: number;
  max_pages: number;
  bleed_mm: number;
  safe_mm: number;
  page_margins_mm?: { left?: number; right?: number; top?: number; bottom?: number } | null;
  allowed_fonts: string[];
  allowed_frames: string[];
  spine_mode?: "cloudprinter_formula" | "manual";
  paper_bulk_type?: PaperBulkType | "";
  cover_thickness_type?: CoverThicknessType | "";
  paper_weight_gsm?: number;
  spine_override_mm?: number | null;
  spine_extra_mm?: number;
  cover_geometry_type?: CoverGeometryType;
  cover_wrap_mm?: number;
  cover_squeeze_mm?: number;
  cover_overlap_mm?: number;
};

type CompatibleVariantsResponse = {
  sku: string;
  compatibility: {
    format_key: string;
    cover_mode: string;
    content_layout: string;
    output_mode: string;
    cover_thickness_type?: string;
    cover_geometry_type?: string;
  };
  current: ProductConfig;
  variants: Array<VariantProduct>;
};

type ProjectVariantChangeResponse = {
  project_id: number;
  public_id: string;
  previous_sku: string | null;
  sku: string;
  changed: boolean;
  product: ProductConfig;
  requires_cover_resync?: boolean;
  requires_cover_review?: boolean;
  cover_geometry_changed?: boolean;
};

type FinishKey = "gloss" | "matte" | "unknown";
type BindingKey = "hardcover" | "softcover" | "unknown";
type VariantProduct = ProductConfig & {
  is_current?: boolean;
  requires_cover_review?: boolean;
  cover_geometry_changed?: boolean;
};

function getPageWidthMm(formats: any[], formatKey: string, pageWidthMm?: number) {
  const explicit = Number(pageWidthMm ?? 0);
  if (explicit > 0) return explicit;
  const fmt = formats.find((f: any) => f.id === formatKey);
  return Number(fmt?.page_mm?.width ?? 210);
}

function getPageHeightMm(formats: any[], formatKey: string, pageHeightMm?: number) {
  const explicit = Number(pageHeightMm ?? 0);
  if (explicit > 0) return explicit;
  const fmt = formats.find((f: any) => f.id === formatKey);
  return Number(fmt?.page_mm?.height ?? 297);
}

function formatKeyToGroup(formatKey: string): FormatGroup {
  const key = String(formatKey || "").toLowerCase();
  if (key.includes("a5") && key.includes("landscape")) return "a5_landscape";
  if (key.includes("a5")) return "a5_portrait";
  if (key.includes("landscape")) return "a4_landscape";
  return "a4_portrait";
}

function templateMarginsFromMeta(margins: any, safeMm: number) {
  return {
    left: Number(margins?.left ?? margins?.outer ?? safeMm),
    right: Number(margins?.right ?? margins?.outer ?? safeMm),
    top: Number(margins?.top ?? safeMm),
    bottom: Number(margins?.bottom ?? safeMm),
  };
}

function productTitleForLocale(product: ProductConfig | null, locale: string, fallbackSku: string) {
  if (!product) return fallbackSku;
  return (product.title_i18n && product.title_i18n[locale]) || product.title || product.sku || fallbackSku;
}

function productFormatLabel(formatKey: string, locale: string) {
  const key = String(formatKey || "").toLowerCase();
  const isPl = locale === "pl";
  if (key.includes("s210") || key.includes("square") || key.includes("21x21")) {
    return isPl ? "Kwadrat 21 x 21 cm" : "Square 21 x 21 cm";
  }
  if (key.includes("a5") && key.includes("landscape")) return isPl ? "A5 pozioma" : "A5 landscape";
  if (key.includes("a5")) return isPl ? "A5 pionowa" : "A5 portrait";
  if (key.includes("a4") && key.includes("landscape")) return isPl ? "A4 pozioma" : "A4 landscape";
  if (key.includes("a4")) return isPl ? "A4 pionowa" : "A4 portrait";
  return formatKey || (isPl ? "Format produktu" : "Product format");
}

function bindingKeyFromProduct(product: ProductConfig | null): BindingKey {
  const thickness = String(product?.cover_thickness_type || "");
  const sku = String(product?.sku || "").toLowerCase();
  if (thickness.startsWith("hardcover") || sku.includes("hardcover")) return "hardcover";
  if (thickness.includes("softcover") || sku.includes("softcover")) return "softcover";
  return "unknown";
}

function bindingLabel(binding: BindingKey, locale: string) {
  const isPl = locale === "pl";
  if (binding === "hardcover") return isPl ? "Twarda okładka" : "Hardcover";
  if (binding === "softcover") return isPl ? "Miękka okładka" : "Softcover";
  return isPl ? "Okładka" : "Cover";
}

function productBindingLabel(product: ProductConfig | null, locale: string) {
  return bindingLabel(bindingKeyFromProduct(product), locale);
}

function finishFromToken(token?: string): FinishKey {
  const t = String(token || "").toLowerCase();
  if (["gloss", "glossy", "blysk", "błysk", "blyszczacy", "błyszczący"].includes(t)) return "gloss";
  if (["mat", "matt", "matte", "mcs", "silk", "satyna", "satynowy"].includes(t)) return "matte";
  return "unknown";
}

function skuTokens(product: ProductConfig | null) {
  return String(product?.sku || "")
    .toLowerCase()
    .split(/[-_\s]+/)
    .filter(Boolean);
}

function coverFinishFromProduct(product: ProductConfig | null): FinishKey {
  const tokens = skuTokens(product);
  const bindingIdx = tokens.findIndex((t) => t === "hardcover" || t === "softcover");
  if (bindingIdx >= 0) {
    const fromNext = finishFromToken(tokens[bindingIdx + 1]);
    if (fromNext !== "unknown") return fromNext;
  }

  const coverIdx = tokens.findIndex((t) => t === "cover" || t === "okladka" || t === "okładka");
  if (coverIdx >= 0) {
    const fromCover = finishFromToken(tokens[coverIdx + 1]);
    if (fromCover !== "unknown") return fromCover;
  }

  const hasPaperMarker = tokens.includes("paper") || tokens.includes("papier");
  const last = finishFromToken(tokens[tokens.length - 1]);
  return !hasPaperMarker ? last : "unknown";
}

function paperFinishFromProduct(product: ProductConfig | null): FinishKey {
  const tokens = skuTokens(product);
  const paperIdx = tokens.findIndex((t) => t === "paper" || t === "papier");
  if (paperIdx >= 0) {
    const fromPaper = finishFromToken(tokens[paperIdx + 1]);
    if (fromPaper !== "unknown") return fromPaper;
  }

  const bulk = String(product?.paper_bulk_type || "").toUpperCase();
  if (bulk === "MCG") return "gloss";
  if (bulk === "MCS") return "matte";
  return "unknown";
}

function finishLabel(finish: FinishKey, locale: string) {
  const isPl = locale === "pl";
  if (finish === "gloss") return isPl ? "błyszcząca" : "glossy";
  if (finish === "matte") return isPl ? "matowa" : "matte";
  return isPl ? "standard" : "standard";
}

function paperLabel(finish: FinishKey, locale: string) {
  const isPl = locale === "pl";
  if (finish === "gloss") return isPl ? "Błyszczący" : "Glossy";
  if (finish === "matte") return isPl ? "Matowy" : "Matte";
  return isPl ? "Standard" : "Standard";
}

function productCoverLabel(product: ProductConfig | null, locale: string) {
  const binding = productBindingLabel(product, locale);
  const finish = coverFinishFromProduct(product);
  return finish === "unknown" ? binding : `${binding} ${finishLabel(finish, locale)}`;
}

function productVariantLabel(product: ProductConfig, locale: string) {
  return `${productCoverLabel(product, locale)} · ${paperLabel(paperFinishFromProduct(product), locale)}`;
}

function coverChoiceKey(product: ProductConfig | null) {
  return `${bindingKeyFromProduct(product)}:${coverFinishFromProduct(product)}`;
}

function filterCoverChoiceProducts(products: VariantProduct[]) {
  const knownFinishByBinding = new Map<BindingKey, boolean>();
  for (const product of products) {
    const binding = bindingKeyFromProduct(product);
    if (coverFinishFromProduct(product) !== "unknown") {
      knownFinishByBinding.set(binding, true);
    } else if (!knownFinishByBinding.has(binding)) {
      knownFinishByBinding.set(binding, false);
    }
  }

  return products.filter((product) => {
    const binding = bindingKeyFromProduct(product);
    const finish = coverFinishFromProduct(product);
    return finish !== "unknown" || knownFinishByBinding.get(binding) !== true;
  });
}

function coverGeometryKey(product: ProductConfig | null) {
  return [
    String(product?.cover_thickness_type || ""),
    String(product?.cover_geometry_type || ""),
    Number(product?.cover_wrap_mm || 0).toFixed(2),
    Number(product?.cover_squeeze_mm || 0).toFixed(2),
    Number(product?.cover_overlap_mm || 0).toFixed(2),
  ].join("|");
}

function coverGeometryChangedBetweenProducts(current: ProductConfig | null, target: ProductConfig | null) {
  if (!current || !target) return false;
  return coverGeometryKey(current) !== coverGeometryKey(target);
}

// English comment: Theme auto-apply should only run on genuinely empty content drafts.
function isEmptyContentDoc(doc: any) {
  const pages = Array.isArray(doc?.pages) ? doc.pages : [];
  for (const unit of pages) {
    const elements = Array.isArray(unit?.elements) ? unit.elements : [];
    if (elements.length > 0) return false;
  }
  return true;
}

/**
 * Run tasks with a concurrency limit (MVP).
 */
async function runWithLimit<T>(items: T[], limit: number, fn: (item: T) => Promise<void>) {
  const queue = [...items];
  const workers = Array.from({ length: limit }, async () => {
    while (queue.length) {
      const item = queue.shift();
      if (!item) return;
      await fn(item);
    }
  });
  await Promise.all(workers);
}

const apiGet = printApiGet;
const apiPost = printApiPost;
const apiPut = printApiPut;
const apiDelete = printApiDelete;

/**
 * Upload an image to Print API (MinIO) using presigned PUT.
 * Returns asset_id + download_url for preview.
 */
async function uploadImageToPrintApi(
  file: File,
  projectId: number | null
): Promise<{ asset_id: number; download_url: string }> {
  if (!projectId) throw new Error("Missing projectId (create project first).");

  // 1) Ask API for presigned PUT URL
  const presignRes = await fetch("/api/print/assets/presign", {
    method: "POST",
    headers: buildPrintApiHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({
      original_name: file.name,
      mime_type: file.type || "image/jpeg",
      project_id: projectId
    })
  });

  if (!presignRes.ok) throw new Error(`presign failed: ${presignRes.status}`);
  const presign = await presignRes.json();

  // 2) Upload file bytes directly to MinIO via PUT URL
  const putRes = await fetch(presign.upload_url, {
    method: "PUT",
    headers: { "Content-Type": file.type || "application/octet-stream" },
    body: file
  });
  if (!putRes.ok) throw new Error(`upload PUT failed: ${putRes.status}`);

  // 3) Tell API upload is complete so it can read px dimensions
  const completeRes = await fetch(`/api/print/assets/${presign.asset_id}/complete`, {
    method: "POST",
    headers: buildPrintApiHeaders()
  });
  if (!completeRes.ok) throw new Error(`complete failed: ${completeRes.status}`);

  // 4) Get a presigned GET URL for preview in editor
  const dlRes = await fetch(`/api/print/assets/${presign.asset_id}/download-url`, {
    cache: "no-store",
    headers: buildPrintApiHeaders()
  });
  if (!dlRes.ok) throw new Error(`download-url failed: ${dlRes.status}`);
  const dl = await dlRes.json();

  return { asset_id: presign.asset_id, download_url: dl.download_url };
}

/**
 * Infer format_key from SKU safely.
 * In dev/hot-reload sku may be temporarily undefined, so we guard against it.
 */
function inferFormatKeyFromSku(sku?: string): string {
  const s = (sku ?? "").toLowerCase();

  if (s.includes("a4") && s.includes("land")) return "a4_landscape";
  if (s.includes("a4")) return "a4_portrait";
  if (s.includes("a5") && s.includes("land")) return "a5_landscape";
  if (s.includes("a5")) return "a5_portrait";
  if (s.includes("21x21")) return "custom_21x21";
  if (s.includes("21x27")) return "custom_21x27";
  return "a4_portrait";
}

function clamp(n: number, a: number, b: number) {
  return Math.max(a, Math.min(b, n));
}

// English comment: Resolve product spine width using manager settings.
function resolveProductSpineMm(prod: any, pageCount: number) {
  if (prod?.spine_mode === "manual" && prod?.spine_override_mm != null) {
    return Number(prod.spine_override_mm);
  }
  if (
    prod?.paper_bulk_type &&
    prod?.cover_thickness_type &&
    Number(prod?.paper_weight_gsm || 0) > 0
  ) {
    return calculateSpineMm({
      pageCount,
      paperWeightGsm: Number(prod.paper_weight_gsm),
      paperBulkType: prod.paper_bulk_type,
      coverThicknessType: prod.cover_thickness_type,
      extraMm: Number(prod.spine_extra_mm || 0),
    });
  }
  // Fallback only if product not configured yet.
  return 18;
}

// English comment: Resolve product cover geometry settings for cover_meta.
function resolveProductCoverGeometryMeta(prod: any) {
  const coverGeometryType = inferCoverGeometryType({
    coverGeometryType: prod?.cover_geometry_type,
    coverThicknessType: prod?.cover_thickness_type,
  });
  const rawWrap = Number(prod?.cover_wrap_mm || 0);
  const rawSqueeze = Number(prod?.cover_squeeze_mm || 0);
  const rawOverlap = Number(prod?.cover_overlap_mm || 0);
  const looksUnconfigured = coverGeometryType === "casewrap" && rawWrap <= 0 && rawSqueeze <= 0 && rawOverlap <= 0;
  return {
    cover_geometry_type: coverGeometryType,
    cover_wrap_mm: coverGeometryType === "casewrap" ? (looksUnconfigured ? 18 : rawWrap) : 0,
    cover_squeeze_mm: coverGeometryType === "casewrap" ? (looksUnconfigured ? 5 : rawSqueeze) : 0,
    cover_overlap_mm: coverGeometryType === "casewrap" ? (looksUnconfigured ? 3 : rawOverlap) : 0,
  };
}

const SPINE_TITLE_MIN_SPINE_MM = 5;
const SPINE_TITLE_PADDING_MM = 2;
const SPINE_TITLE_DEFAULT_FONT_PT = 18;
const SPINE_TITLE_MIN_FONT_PT = 1;
const MM_TO_PT = 72 / 25.4;

type SpineTitleSettings = {
  enabled: boolean;
  text: string;
  font_family: string;
  font_weight: number;
  italic: boolean;
  color_hex: string;
  padding_mm: number;
  font_size_pt: number;
  rotate_deg: number;
};

function normalizeEditorHexColor(value: unknown, fallback = "#111827") {
  const raw = String(value || "").trim();
  if (/^#[0-9a-fA-F]{6}$/.test(raw)) return raw;
  if (/^#[0-9a-fA-F]{3}$/.test(raw)) {
    return `#${raw[1]}${raw[1]}${raw[2]}${raw[2]}${raw[3]}${raw[3]}`;
  }
  return fallback;
}

function estimateSpineTitleUnits(text: string) {
  return Array.from(text).reduce((sum, char) => {
    if (/\s/.test(char)) return sum + 0.28;
    if (/[A-Z0-9]/.test(char)) return sum + 0.62;
    if (/[mwMW]/.test(char)) return sum + 0.78;
    if (/[ilI.,;:'"`!|]/.test(char)) return sum + 0.3;
    return sum + 0.52;
  }, 0);
}

// English comment: Fit spine title text into the current spine width and cover height.
function fitSpineTitleFontPt(args: {
  text: string;
  spineMm: number;
  panelHeightMm: number;
  paddingMm: number;
  maxFontPt?: number;
}) {
  const text = String(args.text || "").trim();
  const spineMm = Number(args.spineMm || 0);
  if (!text || spineMm < SPINE_TITLE_MIN_SPINE_MM) return 0;

  const paddingMm = Math.max(0, Number(args.paddingMm || 0));
  const usableSpinePt = Math.max(0, (spineMm - paddingMm * 2) * MM_TO_PT * 0.78);
  const usableLengthPt = Math.max(1, (Number(args.panelHeightMm || 0) - paddingMm * 2) * MM_TO_PT);
  const units = Math.max(0.1, estimateSpineTitleUnits(text));
  const maxFontPt = clamp(Number(args.maxFontPt || SPINE_TITLE_DEFAULT_FONT_PT), SPINE_TITLE_MIN_FONT_PT, 72);
  const fittedByLengthPt = usableLengthPt / units;
  const next = Math.min(maxFontPt, usableSpinePt, fittedByLengthPt);

  return Number(Math.max(SPINE_TITLE_MIN_FONT_PT, next).toFixed(1));
}

function normalizeSpineTitleSettings(
  raw: any,
  args: {
    spineMm: number;
    panelHeightMm: number;
    defaultFontFamily?: string;
  }
): SpineTitleSettings {
  const paddingMm = clamp(Number(raw?.padding_mm ?? SPINE_TITLE_PADDING_MM), 0, 10);
  const text = String(raw?.text || "").slice(0, 200);
  const fontFamily = String(raw?.font_family || args.defaultFontFamily || "Raleway");
  const fontWeight = clamp(Number(raw?.font_weight ?? 700), 100, 900);
  const fontSizePt = fitSpineTitleFontPt({
    text,
    spineMm: Number(args.spineMm || 0),
    panelHeightMm: Number(args.panelHeightMm || 0),
    paddingMm,
    maxFontPt: Number(raw?.max_font_size_pt || SPINE_TITLE_DEFAULT_FONT_PT),
  });

  return {
    enabled: Boolean(raw?.enabled),
    text,
    font_family: fontFamily,
    font_weight: fontWeight,
    italic: Boolean(raw?.italic),
    color_hex: normalizeEditorHexColor(raw?.color_hex, "#111827"),
    padding_mm: paddingMm,
    font_size_pt: fontSizePt,
    rotate_deg: Number(raw?.rotate_deg ?? -90),
  };
}

function spineTitleEquals(a: any, b: SpineTitleSettings) {
  return (
    Boolean(a?.enabled) === b.enabled &&
    String(a?.text || "") === b.text &&
    String(a?.font_family || "") === b.font_family &&
    Number(a?.font_weight ?? 0) === b.font_weight &&
    Boolean(a?.italic) === b.italic &&
    normalizeEditorHexColor(a?.color_hex, "#111827") === b.color_hex &&
    Number(a?.padding_mm ?? SPINE_TITLE_PADDING_MM) === b.padding_mm &&
    Number(a?.font_size_pt ?? 0) === b.font_size_pt &&
    Number(a?.rotate_deg ?? -90) === b.rotate_deg
  );
}

function syncCoverDataForSpineMeta(args: {
  coverData: any;
  contentData: any;
  product: ProductConfig | null;
  formats: EditorConfig["formats"];
  defaultFontFamily?: string;
}) {
  const coverData = args.coverData;
  if (!coverData) return coverData;

  const currentMeta = coverData.cover_meta || {};
  const pageCount = getContentPhysicalPageCount(args.contentData);
  const nextSpineMm = args.product
    ? resolveProductSpineMm(args.product, pageCount)
    : Number(currentMeta.spine_mm || 18);
  const nextCoverGeometry = args.product ? resolveProductCoverGeometryMeta(args.product) : null;
  const formatKey = String(currentMeta.format_key || args.product?.format_key || "a4_portrait");
  const panelHeightMm = getPageHeightMm(args.formats || [], formatKey);

  let changed = false;
  const nextMeta: any = { ...currentMeta };
  if (Math.abs(Number(currentMeta.spine_mm ?? 0) - nextSpineMm) >= 0.01) {
    nextMeta.spine_mm = nextSpineMm;
    changed = true;
  }
  if (nextCoverGeometry) {
    for (const [key, value] of Object.entries(nextCoverGeometry)) {
      if (key === "cover_geometry_type") {
        if (String(currentMeta[key] || "") !== String(value)) {
          nextMeta[key] = value;
          changed = true;
        }
      } else if (Math.abs(Number(currentMeta[key] ?? 0) - Number(value || 0)) >= 0.01) {
        nextMeta[key] = value;
        changed = true;
      }
    }
  }

  if (currentMeta.spine_title) {
    const nextTitle = normalizeSpineTitleSettings(currentMeta.spine_title, {
      spineMm: nextSpineMm,
      panelHeightMm,
      defaultFontFamily: args.defaultFontFamily,
    });
    if (!spineTitleEquals(currentMeta.spine_title, nextTitle)) {
      nextMeta.spine_title = nextTitle;
      changed = true;
    }
  }

  return changed ? { ...coverData, cover_meta: nextMeta } : coverData;
}

// English comment: Count physical content pages from units (single=1, spread=2).
function getContentPhysicalPageCount(contentData: any) {
  const units = Array.isArray(contentData?.pages) ? contentData.pages : [];
  return units.reduce((sum: number, u: any) => sum + (String(u?.kind || "single") === "spread" ? 2 : 1), 0);
}

// English comment: Return true if the content unit has any meaningful element worth preloading a thumbnail for.
function unitHasMeaningfulContent(unit: any) {
  const els: any[] = Array.isArray(unit?.elements) ? unit.elements : [];
  return els.some((el) => {
    if (!el || el?._ghost) return false;
    // Ignore pure backgrounds.
    if (el.type === "background" || el.is_background) return false;
    // Empty text should not count.
    if (el.type === "text") {
      return Boolean(String(el.text || "").trim());
    }
    // Image placeholders still count as useful visual content.
    if (el.type === "image") return true;
    // Shapes/rects/etc. count as visual content.
    return true;
  });
}

// English comment: Build the same thumb key that RightPagesPanel expects.
function getUnitThumbKey(unit: any, idx: number) {
  const unitId = String(unit?.unit_id || `u_${idx}`);
  return `content:${unitId}`;
}

function getUnitImageSignature(unit: any) {
  const els: any[] = Array.isArray(unit?.elements) ? unit.elements : [];
  return els
    .filter((el) => el?.type === "image" && !el?._ghost)
    .map((el) => `${Number(el.asset_id || 0)}:${String(el.image_url || el.clipart_id || "")}`)
    .filter((part) => part !== "0:")
    .join("|");
}

/**
 * Create a default cover document using product config.
 */
function defaultCoverDataFromProduct(prod: ProductConfig): any {
  const mode = prod.cover_mode || "spread";
  const coverGeometry = resolveProductCoverGeometryMeta(prod);
  return {
    cover_meta: {
      format_key: prod.format_key,
      cover_mode: mode,
      spine_mm: resolveProductSpineMm(prod, Number(prod.min_pages ?? 24)),
      bleed_mm: Number(prod.bleed_mm ?? 3),
      safe_mm: Number(prod.safe_mm ?? 5),
      ...coverGeometry,
    },
    pages:
      mode === "separate"
        ? [{ panel: "back", elements: [] }, { panel: "front", elements: [] }]
        : [{ panel: "spread", elements: [] }],
  };
}

/**
 * Ensure every element has a stable id (string).
 * Used during draft load migration to normalize old data.
 */
function ensureElementIds(docData: any): any {
  const pages = Array.isArray(docData?.pages) ? docData.pages : [];
  let changed = false;

  const newPages = pages.map((p: any) => {
    const els = Array.isArray(p?.elements) ? p.elements : [];
    const newEls = els.map((el: any, idx: number) => {
      if (el?.id) return el;
      changed = true;
      return { ...el, id: `el_${Math.random().toString(16).slice(2)}_${idx}` };
    });
    return { ...p, elements: newEls };
  });

  return changed ? { ...docData, pages: newPages } : docData;
}

/**
 * Ensure every content unit has a stable id for drag-and-drop ordering.
 */
function ensureUnitIds(doc: any) {
  const pages = Array.isArray(doc?.pages) ? doc.pages : [];
  let changed = false;

  const newPages = pages.map((u: any) => {
    if (u?.unit_id) return u;
    changed = true;
    return { ...u, unit_id: `u_${Math.random().toString(16).slice(2)}` };
  });

  return changed ? { ...doc, pages: newPages } : doc;
}

/**
 * Collect unique asset ids from a document snapshot.
 */
function elementCanUsePreviewImage(el: any) {
  return el?.type === "image" || el?.type === "background" || Boolean(el?.is_background);
}

function collectAssetIds(docData: any) {
  const pages = Array.isArray(docData?.pages) ? docData.pages : [];
  const ids = new Set<number>();

  for (const p of pages) {
    const els = Array.isArray(p?.elements) ? p.elements : [];
    for (const el of els) {
      if (!elementCanUsePreviewImage(el)) continue;
      const assetId = Number(el.asset_id || 0);
      if (assetId > 0) ids.add(assetId);
    }
  }

  return Array.from(ids);
}

function collectImagePreviewUrls(...docs: any[]) {
  const urls = new Set<string>();
  for (const docData of docs) {
    const pages = Array.isArray(docData?.pages) ? docData.pages : [];
    for (const p of pages) {
      const els = Array.isArray(p?.elements) ? p.elements : [];
      for (const el of els) {
        if (!elementCanUsePreviewImage(el)) continue;
        const url = String(el.image_url || "");
        if (url) urls.add(url);
      }
    }
  }
  return Array.from(urls);
}

// English comment: Re-attach preview image URLs to image elements using asset_id.
function patchDocImageUrls(docData: any, urlByAssetId: Record<number, string>) {
  if (!docData || !Array.isArray(docData.pages)) return docData;

  let changed = false;
  const newPages = docData.pages.map((p: any) => {
    const els = Array.isArray(p?.elements) ? p.elements : [];
    let pageChanged = false;

    const newEls = els.map((el: any) => {
      if (!elementCanUsePreviewImage(el) || !el.asset_id) return el;
      const assetId = Number(el.asset_id || 0);
      const nextUrl = urlByAssetId[assetId];
      if (!nextUrl || el.image_url === nextUrl) return el;

      changed = true;
      pageChanged = true;
      return { ...el, image_url: nextUrl };
    });

    return pageChanged ? { ...p, elements: newEls } : p;
  });

  return changed ? { ...docData, pages: newPages } : docData;
}

// English comment: Return true when persisted image assets are missing preview-only image URLs.
function docHasImageAssetsMissingUrl(docData: any) {
  const pages = Array.isArray(docData?.pages) ? docData.pages : [];
  for (const p of pages) {
    const els = Array.isArray(p?.elements) ? p.elements : [];
    for (const el of els) {
      if (!elementCanUsePreviewImage(el)) continue;
      const assetId = Number(el.asset_id || 0);
      if (assetId > 0 && !el.image_url) return true;
    }
  }
  return false;
}

/**
 * Rehydrate preview URLs for image elements in a doc.
 * NOTE: do not persist image_url to backend (strip before autosave).
 */
async function hydrateDocImageUrls(docData: any) {
  const ids = collectAssetIds(docData);
  if (ids.length === 0) return docData;

  const batch = await apiPost<{ items: Array<{ asset_id: number; download_url: string }> }>(
    "assets/download-urls",
    { asset_ids: ids }
  );

  const urlByAssetId: Record<number, string> = {};
  const items = Array.isArray(batch?.items) ? batch.items : [];
  for (const it of items) {
    const assetId = Number(it?.asset_id || 0);
    const url = String(it?.download_url || "");
    if (assetId > 0 && url) urlByAssetId[assetId] = url;
  }

  return patchDocImageUrls(docData, urlByAssetId);
}

/**
 * Remove transient preview fields before persisting to backend.
 * - image_url is presigned and expires, so it must NOT be saved.
 */
function stripPreviewFields(docData: any) {
  const pages = Array.isArray(docData?.pages) ? docData.pages : [];
  const newPages = pages.map((p: any) => ({
    ...p,
    elements: (p.elements || []).map((el: any) => {
      if (elementCanUsePreviewImage(el) && "image_url" in el) {
        const { image_url, ...rest } = el;
        return rest;
      }
      return el;
    }),
  }));
  return { ...docData, pages: newPages };
}

/**
 * Get active document data + setter + active page index.
 */
function getActiveDocCtx(store: any) {
  const doc = store.activeDoc as "content" | "cover";
  const docData = doc === "content" ? store.contentData : store.coverData;
  const setDocData = doc === "content" ? store.setContentData : store.setCoverData;
  const pageIndex = doc === "content" ? store.contentUnitIndex : store.coverPageIndex;

  return { doc, docData, setDocData, pageIndex };
}

/**
 * Find an element by id in a document.
 */
function findElementById(docData: any, elementId: string) {
  const pages = Array.isArray(docData?.pages) ? docData.pages : [];
  for (let pi = 0; pi < pages.length; pi++) {
    const els = Array.isArray(pages[pi]?.elements) ? pages[pi].elements : [];
    for (let ei = 0; ei < els.length; ei++) {
      if (els[ei]?.id === elementId) return { pageIndex: pi, elementIndex: ei, el: els[ei] };
    }
  }
  return null;
}

function isImagePlaceholderElement(el: any) {
  return (
    el?.type === "image" &&
    String(el?.source || "") !== "clipart" &&
    (el?.placeholder === true || String(el?.placeholder_kind || "") === "image")
  );
}

/**
 * Duplicate selected element (MVP):
 * - clones the element
 * - assigns a new id
 * - offsets by +5mm, +5mm
 * - inserts into the same page
 */
function duplicateSelected(store: any) {
  const doc = store.selected.doc;
  const id = store.selected.elementId;
  if (!id) return;

  const docData = doc === "content" ? store.contentData : store.coverData;
  if (!docData) return;

  const found = findElementById(docData, id);
  if (!found) return;

  const { pageIndex, el } = found;

  const pages = Array.isArray(docData.pages) ? [...docData.pages] : [{ elements: [] }];
  const page = pages[pageIndex] || { elements: [] };
  const els = Array.isArray(page.elements) ? [...page.elements] : [];

  const clone = {
    ...el,
    id: `el_${Math.random().toString(16).slice(2)}`,
    x_mm: Number(el.x_mm ?? 0) + 5,
    y_mm: Number(el.y_mm ?? 0) + 5
  };

  els.push(clone);
  pages[pageIndex] = { ...page, elements: els };

  const newDoc = { ...docData, pages };

  if (doc === "content") store.setContentData(newDoc);
  else store.setCoverData(newDoc);

  store.selectElement(doc, clone.id);
}

function cloneJson<T>(value: T): T {
  return value == null ? value : JSON.parse(JSON.stringify(value));
}

/**
 * Compute a default frame size in mm based on image aspect ratio.
 * Keeps a reasonable max side so portrait images become vertical, etc.
 */
function defaultImageFrameMm(widthPx?: number | null, heightPx?: number | null) {
  const maxSideMm = 90; // choose 80-120 depending on your UX
  const w = Number(widthPx || 0);
  const h = Number(heightPx || 0);
  if (!w || !h) return { w_mm: 80, h_mm: 60 };

  const aspect = w / h;
  if (aspect >= 1) {
    return { w_mm: maxSideMm, h_mm: maxSideMm / aspect };
  }
  return { w_mm: maxSideMm * aspect, h_mm: maxSideMm };
}

function getClipartPublicUrl(clipartId: string) {
  const clean = String(clipartId || "")
    .split("/")
    .filter(Boolean)
    .map((part) => encodeURIComponent(part))
    .join("/");
  return clean ? `/clipart/${clean}` : "";
}

function centerPosMm(artWmm: number, artHmm: number, wMm: number, hMm: number) {
  return { x_mm: (artWmm - wMm) / 2, y_mm: (artHmm - hMm) / 2 };
}

function SpineTitlePanel({
  locale,
  coverMode,
  spineMm,
  title,
  fonts,
  readOnly,
  onChange,
}: {
  locale: string;
  coverMode: string;
  spineMm: number;
  title: SpineTitleSettings;
  fonts: FontItem[];
  readOnly?: boolean;
  onChange: (patch: Partial<SpineTitleSettings>) => void;
}) {
  const isPl = locale === "pl";
  const spreadOk = coverMode === "spread";
  const spineOk = spineMm >= SPINE_TITLE_MIN_SPINE_MM;
  const canUse = spreadOk && spineOk;
  const controlsDisabled = readOnly || !canUse || !title.enabled;

  return (
    <div className="space-y-3">
      <div>
        <div className="text-xs font-semibold uppercase tracking-wide text-white/45">
          {isPl ? "Tytuł na grzbiecie" : "Spine title"}
        </div>
        <div className="mt-1 text-xs leading-5 text-white/60">
          {isPl
            ? `Aktualny grzbiet: ${spineMm.toFixed(2)} mm. Tekst ma 2 mm paddingu i automatyczny rozmiar.`
            : `Current spine: ${spineMm.toFixed(2)} mm. Text uses 2 mm padding and automatic sizing.`}
        </div>
      </div>

      {!spreadOk ? (
        <div className="rounded-lg border border-white/10 bg-white/5 p-3 text-xs leading-5 text-white/70">
          {isPl
            ? "Tytuł grzbietu jest dostępny tylko dla okładki jako spread."
            : "Spine title is available only for spread covers."}
        </div>
      ) : !spineOk ? (
        <div className="rounded-lg border border-amber-300/30 bg-amber-300/10 p-3 text-xs leading-5 text-amber-100">
          {isPl
            ? "Grzbiet jest węższy niż 5 mm, więc tytuł jest wyłączony."
            : "The spine is narrower than 5 mm, so the title is disabled."}
        </div>
      ) : null}

      <label className="flex items-center justify-between gap-3 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white">
        <span>{isPl ? "Pokaż tytuł" : "Show title"}</span>
        <input
          type="checkbox"
          checked={title.enabled && canUse}
          disabled={readOnly || !canUse}
          onChange={(e) => {
            onChange({
              enabled: e.target.checked,
              text: title.text || (isPl ? "Tytuł książki" : "Book title"),
            });
          }}
        />
      </label>

      <div className="space-y-2">
        <label className="text-xs font-medium text-white/70">{isPl ? "Tekst" : "Text"}</label>
        <input
          className="w-full rounded-lg border border-white/10 bg-white px-3 py-2 text-sm text-[#272727] outline-none disabled:opacity-50"
          value={title.text}
          maxLength={200}
          disabled={controlsDisabled}
          onChange={(e) => onChange({ text: e.target.value })}
          placeholder={isPl ? "Tytuł książki" : "Book title"}
        />
      </div>

      <div className="space-y-2">
        <label className="text-xs font-medium text-white/70">{isPl ? "Font" : "Font"}</label>
        <FontPicker
          fonts={fonts}
          value={title.font_family}
          disabled={controlsDisabled}
          onSelect={(family) => {
            const nextFont = fonts.find((font) => font.family === family);
            onChange({
              font_family: family,
              font_weight: nextFont?.variants?.bold ? 700 : 400,
              italic: false,
            });
          }}
        />
      </div>

      <div className="grid grid-cols-[1fr_auto] items-end gap-3">
        <div className="space-y-2">
          <label className="text-xs font-medium text-white/70">{isPl ? "Kolor" : "Color"}</label>
          <input
            className="h-10 w-full rounded-lg border border-white/10 bg-white px-2 py-1 disabled:opacity-50"
            type="color"
            value={title.color_hex}
            disabled={controlsDisabled}
            onChange={(e) => onChange({ color_hex: e.target.value })}
          />
        </div>
        <div className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-right text-xs leading-5 text-white/70">
          <div>{isPl ? "Rozmiar" : "Size"}</div>
          <div className="font-semibold text-white">{title.font_size_pt > 0 ? `${title.font_size_pt} pt` : "-"}</div>
        </div>
      </div>
    </div>
  );
}

export default function EditorClient({ sku }: { sku: string }) {
  const t = useTranslations("editor");
  const locale = useLocale();
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();
  const projectPublicId = searchParams.get("project") || undefined;
  const sessionToken = searchParams.get("session") || undefined;
  const inviteToken = searchParams.get("invite") || undefined;
  const themeId = searchParams.get("theme") || undefined;
  const mode = useMemo(() => (projectPublicId ? "resume" : "new"), [projectPublicId]);

  const store = useEditorStore();

  const [config, setConfig] = useState<EditorConfig | null>(null);
  const [product, setProduct] = useState<ProductConfig | null>(null);
  const [project, setProjectState] = useState<Project | null>(null);
  const [error, setError] = useState<string | null>(null);
  // English comment: Access gate shown when project link is opened without a valid session.
  const [accessRequired, setAccessRequired] = useState<null | {
    sku: string;
    projectPublicId: string;
    message: string;
  }>(null);
  const [uploading, setUploading] = useState(false);
  const [imageLibraryRefreshNonce, setImageLibraryRefreshNonce] = useState(0);
  const [bulkProgress, setBulkProgress] = useState<{ total: number; done: number; running: boolean }>({
    total: 0,
    done: 0,
    running: false
  });

  const [lastSaveAt, setLastSaveAt] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveAsOpen, setSaveAsOpen] = useState(false);
  const [copyingProject, setCopyingProject] = useState(false);
  const [copyError, setCopyError] = useState<string | null>(null);
  const saveAsPausedRef = useRef(false);
  const copyRequestRef = useRef(false);
  const [saveReturnError, setSaveReturnError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; kind: "ok" | "error" } | null>(null);
  const [orderDialogMode, setOrderDialogMode] = useState<"review" | "variant" | null>(null);
  const [compatibleVariants, setCompatibleVariants] = useState<VariantProduct[]>([]);
  const [compatibleVariantsForSku, setCompatibleVariantsForSku] = useState<string | null>(null);
  const [variantLoading, setVariantLoading] = useState(false);
  const [variantError, setVariantError] = useState<string | null>(null);
  const [selectedVariantSku, setSelectedVariantSku] = useState<string | null>(null);
  const [coverChangeAccepted, setCoverChangeAccepted] = useState(false);
  const [changingVariant, setChangingVariant] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [showOnboardingGuide, setShowOnboardingGuide] = useState(false);
  const [helpAction, setHelpAction] = useState<HelpAction | null>(null);
  const [helpActionNonce, setHelpActionNonce] = useState(0);
  const [showShare, setShowShare] = useState(false);
  const [sessionRole, setSessionRole] = useState<string | null>(null);
  const [presence, setPresence] = useState<PresenceRow[]>([]);
  const [lock, setLock] = useState<ProjectLockState | null>(null);
  const [canEditNow, setCanEditNow] = useState(false);
  // English comment: Guest name prompt state for invite sessions.
  const [guestNamePromptOpen, setGuestNamePromptOpen] = useState(false);
  const [guestNameDraft, setGuestNameDraft] = useState("");
  const [pendingInvite, setPendingInvite] = useState<string | null>(null);
  // English comment: Used to resume bootstrap after guest name prompt.
  const [inviteBootstrapNonce, setInviteBootstrapNonce] = useState(0);
  const [fontsReady, setFontsReady] = useState(false);
  const canvasViewportRef = useRef<HTMLDivElement | null>(null);
  const preloadedImageUrlsRef = useRef<Set<string>>(new Set());
  const initialImagePreloadDoneRef = useRef(false);
  const imagePreloadProjectIdRef = useRef<number | null>(null);
  const projectImagePreloadRunRef = useRef(0);
  const projectImagePreloadTimerRef = useRef<number | null>(null);
  const thumbImageSignaturesRef = useRef<Record<string, string>>({});
  const [projectImagePreload, setProjectImagePreload] = useState<{
    running: boolean;
    total: number;
    done: number;
    label?: string;
  }>({
    running: false,
    total: 0,
    done: 0
  });
  const [canvasViewport, setCanvasViewport] = useState({ w: 800, h: 600 });
  const [manualZoom, setManualZoom] = useState(1);
  const [zoomTouched, setZoomTouched] = useState(false);
  const [fitZoom, setFitZoom] = useState(1);
  const [stageMetrics, setStageMetrics] = useState<{ stageW: number; stageH: number } | null>(null);
  const [selectionCenter, setSelectionCenter] = useState<{ x: number; y: number } | null>(null);
  const [propsDrawerOpen, setPropsDrawerOpen] = useState(false);
  const [showImages, setShowImages] = useState(false);
  const [toolsCollapsed, setToolsCollapsed] = useState(false);
  const [mobilePagesOpen, setMobilePagesOpen] = useState(false);
  const [mobileToolAction, setMobileToolAction] = useState<HelpAction | null>(null);
  const [mobileToolActionNonce, setMobileToolActionNonce] = useState(0);
  const [bgColor, setBgColor] = useState("#fefefe");
  const [thumbs, setThumbs] = useState<Record<string, string>>({});
  // English comment: Queue of content unit indices that should get thumbnails pre-rendered.
  const [thumbPreloadQueue, setThumbPreloadQueue] = useState<number[]>([]);
  const [projectSettings, setProjectSettings] = useState<any>({});
  const [generatedThemeTemplates, setGeneratedThemeTemplates] = useState<ThemeTemplate[]>([]);
  const [themeTemplateCreateBusy, setThemeTemplateCreateBusy] = useState(false);
  const [themeTemplateDeleteBusyId, setThemeTemplateDeleteBusyId] = useState<string | null>(null);
  const coverMode = store.coverData?.cover_meta?.cover_mode || product?.cover_mode || "spread";
  const allowedFontsSet = useMemo(() => new Set(product?.allowed_fonts || []), [product]);
  const allowedFramesSet = useMemo(() => new Set(product?.allowed_frames || []), [product]);
  const contentFormatKey = String(store.contentData?.meta?.format_key || product?.format_key || "a4_portrait");
  const activeFormatGroup = formatKeyToGroup(contentFormatKey);
  const allThemeTemplates = useMemo(
    () => [...THEME_TEMPLATES, ...generatedThemeTemplates],
    [generatedThemeTemplates]
  );
  const visibleThemeTemplates = useMemo(
    () => allThemeTemplates.filter((theme) => !theme.format_group || theme.format_group === activeFormatGroup),
    [activeFormatGroup, allThemeTemplates]
  );
  const contentPageWidthMm = config
    ? getPageWidthMm(config.formats, contentFormatKey, store.contentData?.meta?.page_mm?.width)
    : 210;
  const canShare = sessionRole === "admin" || sessionRole === "user";
  const editorCapable = sessionRole === "admin" || sessionRole === "user" || sessionRole === "guest_editor";
  const isViewer = sessionRole === "guest_viewer";
  const readOnly = isViewer || !editorCapable || !canEditNow;
  const canOrder = sessionRole !== "guest_viewer" && sessionRole !== "guest_editor";
  // English comment: Render presence/lock label based on current session role (better UX).
  function presenceLabel() {
    const role = sessionRole || "";
    const lockedBy = lock?.locked && !lock?.mine
      ? (lock.by || (locale === "pl" ? "inna osoba" : "someone"))
      : null;
    if (role === "guest_viewer" || role === "guest_editor") {
      return lockedBy
        ? locale === "pl" ? `Zablokowane przez ${lockedBy}` : `Locked by ${lockedBy}`
        : locale === "pl" ? "Tylko do odczytu" : "Read-only";
    }
    const online = (presence || []).map((p) => p?.label).filter(Boolean);
    const onlineText = online.length
      ? locale === "pl" ? `Aktywni: ${online.join(", ")}` : `Online: ${online.join(", ")}`
      : locale === "pl" ? "Aktywni: -" : "Online: -";
    if (lockedBy) {
      return locale === "pl"
        ? `${onlineText} · Zablokowane przez ${lockedBy}`
        : `${onlineText} · Locked by ${lockedBy}`;
    }
    if (lock?.locked && lock?.mine) {
      return locale === "pl" ? `${onlineText} · Edytujesz` : `${onlineText} · You are editing`;
    }
    return onlineText;
  }
  const contentPageHeightMm = config
    ? getPageHeightMm(config.formats, contentFormatKey, store.contentData?.meta?.page_mm?.height)
    : 297;
  const activeContentUnit = Array.isArray(store.contentData?.pages)
    ? store.contentData.pages[store.contentUnitIndex] || null
    : null;
  const isActiveContentSpread = store.activeDoc === "content" && String(activeContentUnit?.kind || "single") === "spread";
  const filteredFonts = useMemo(
    () => (config ? config.fonts.filter((f: any) => allowedFontsSet.size === 0 || allowedFontsSet.has(f.family)) : []),
    [config, allowedFontsSet]
  );
  const filteredFrames = useMemo(
    () => (config ? config.frames.filter((fr: any) => allowedFramesSet.size === 0 || allowedFramesSet.has(fr.id)) : []),
    [config, allowedFramesSet]
  );
  const editorCanvasReady = Boolean(config && product && project && store.contentData && store.coverData);
  const onboardingReady = editorCanvasReady && !readOnly;
  const productTitle = productTitleForLocale(product, locale, sku);
  const coverFormatKey = String(store.coverData?.cover_meta?.format_key || product?.format_key || "a4_portrait");
  const coverPanelHeightMm = config
    ? getPageHeightMm(config.formats, coverFormatKey, store.coverData?.cover_meta?.page_mm?.height)
    : 297;
  const coverSpineMm = Number(
    store.coverData?.cover_meta?.spine_mm ||
      (product ? resolveProductSpineMm(product, getContentPhysicalPageCount(store.contentData)) : 18)
  );
  const coverSpineTitle = useMemo(
    () =>
      normalizeSpineTitleSettings(store.coverData?.cover_meta?.spine_title || {}, {
        spineMm: coverSpineMm,
        panelHeightMm: coverPanelHeightMm,
        defaultFontFamily: filteredFonts[0]?.family || "Raleway",
      }),
    [coverPanelHeightMm, coverSpineMm, filteredFonts, store.coverData?.cover_meta?.spine_title]
  );
  // Prevent double create-project under Strict Mode in dev
  const createdProjectRef = useRef<Project | null>(null);
  const copiedTextRef = useRef<any | null>(null);
  const authBootstrapRef = useRef(false);
  const skippedInviteRef = useRef<string | null>(null);
  const rehydrateImageUrlsInFlightRef = useRef(false);
  const [authReady, setAuthReady] = useState(false);
  const [invitedProjectId, setInvitedProjectId] = useState<number | null>(null);
  const paginationRef = useRef<HTMLDivElement | null>(null);
  const [paginationReserve, setPaginationReserve] = useState(0);
  // English comment: One-level undo snapshot for theme application.
  const themeUndoRef = useRef<null | { contentData: any; contentUnitIndex: number }>(null);
  const [canUndoTheme, setCanUndoTheme] = useState(false);
  const canvasBottomReserve = store.activeDoc === "content" ? paginationReserve : 0;
  const currentCanvasZoom = zoomTouched ? manualZoom : fitZoom;
  const canvasWrapperWidth = stageMetrics
    ? Math.max(canvasViewport.w, Math.ceil(stageMetrics.stageW * currentCanvasZoom))
    : canvasViewport.w;
  // English comment: Deep clone helper for undo snapshots.
  const deepClone = (v: any) => {
    // @ts-ignore
    if (typeof structuredClone === "function") return structuredClone(v);
    return JSON.parse(JSON.stringify(v));
  };

  useEffect(() => {
    setCompatibleVariants([]);
    setCompatibleVariantsForSku(null);
    setSelectedVariantSku(product?.sku || null);
    setCoverChangeAccepted(false);
    setVariantError(null);
  }, [product?.sku]);

  // English comment: Build the secure shop URL that issues a session and redirects back to the editor.
  function getWpBaseUrl() {
    return (process.env.NEXT_PUBLIC_WP_BASE_URL || "http://localhost:8085").replace(/\/+$/, "");
  }

  function buildOpenEditorShopUrl(sku: string, projectPublicId: string) {
    return `${getWpBaseUrl()}/open-editor/${encodeURIComponent(sku)}/${encodeURIComponent(projectPublicId)}/`;
  }

  function getSyncedCoverDataForPersist(
    coverData: any = store.coverData,
    contentData: any = store.contentData,
    productOverride: ProductConfig | null = product
  ) {
    return syncCoverDataForSpineMeta({
      coverData,
      contentData,
      product: productOverride,
      formats: config?.formats || [],
      defaultFontFamily: filteredFonts[0]?.family || "Raleway",
    });
  }

  function buildDocsForProductVariant(nextProduct: ProductConfig) {
    if (!store.contentData || !store.coverData || !config) return null;

    const fmt = config.formats.find((f: any) => f.id === nextProduct.format_key);
    const pageWmm = Number(fmt?.page_mm?.width ?? store.contentData?.meta?.page_mm?.width ?? 210);
    const pageHmm = Number(fmt?.page_mm?.height ?? store.contentData?.meta?.page_mm?.height ?? 297);
    const prodLayout = normalizeContentLayout(nextProduct.content_layout);
    const bleedMm = Number(nextProduct.bleed_mm ?? fmt?.bleed_mm ?? 3);
    const safeMm = Number(nextProduct.safe_mm ?? fmt?.safe_mm ?? 5);
    const minPages = Number(nextProduct.min_pages ?? 24);

    let nextContent = {
      ...store.contentData,
      meta: {
        ...(store.contentData.meta || {}),
        content_layout: prodLayout,
        format_key: nextProduct.format_key,
        bleed_mm: bleedMm,
        safe_mm: safeMm,
        page_mm: { width: pageWmm, height: pageHmm },
        page_margins_mm: normalizePageMarginsMm(store.contentData.meta?.page_margins_mm),
      },
    };

    if (String(nextContent.meta.layout_mode || "pages") !== "book_spreads") {
      nextContent = migratePagesToBookSpreadsUnits(nextContent, pageWmm, prodLayout);
    } else {
      nextContent.meta = {
        ...(nextContent.meta || {}),
        layout_mode: "book_spreads",
        content_layout: prodLayout,
        page_margins_mm: normalizePageMarginsMm(nextContent.meta?.page_margins_mm),
      };
    }
    nextContent = ensureUnitIds(ensureMinPhysicalPages(nextContent, minPages));

    let nextCover = {
      ...store.coverData,
      cover_meta: {
        ...(store.coverData.cover_meta || {}),
        format_key: nextProduct.format_key,
        bleed_mm: bleedMm,
        safe_mm: safeMm,
        cover_mode: nextProduct.cover_mode || "spread",
        ...resolveProductCoverGeometryMeta(nextProduct),
      },
    };
    nextCover.cover_meta.spine_mm = resolveProductSpineMm(nextProduct, getContentPhysicalPageCount(nextContent));
    nextCover = getSyncedCoverDataForPersist(nextCover, nextContent, nextProduct);

    if (nextCover.cover_meta.cover_mode === "separate" && (!Array.isArray(nextCover.pages) || nextCover.pages.length !== 2)) {
      nextCover = { ...nextCover, pages: [{ panel: "back", elements: [] }, { panel: "front", elements: [] }] };
    }
    if (nextCover.cover_meta.cover_mode === "spread" && (!Array.isArray(nextCover.pages) || nextCover.pages.length < 1)) {
      nextCover = { ...nextCover, pages: [{ panel: "spread", elements: [] }] };
    }

    return {
      contentData: nextContent,
      coverData: nextCover,
    };
  }

  function replaceEditorUrlSku(nextSku: string) {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    const parts = url.pathname.split("/");
    const editorIdx = parts.findIndex((part) => part === "editor");
    if (editorIdx < 0 || !parts[editorIdx + 1]) return;
    parts[editorIdx + 1] = encodeURIComponent(nextSku);
    window.history.replaceState(window.history.state, "", `${parts.join("/")}${url.search}${url.hash}`);
  }

  function updateSpineTitle(patch: Partial<SpineTitleSettings>) {
    if (readOnly) return;
    const coverData = store.coverData;
    if (!coverData) return;
    const current = coverData.cover_meta?.spine_title || coverSpineTitle;
    const nextTitle = normalizeSpineTitleSettings(
      {
        ...current,
        ...patch,
      },
      {
        spineMm: coverSpineMm,
        panelHeightMm: coverPanelHeightMm,
        defaultFontFamily: filteredFonts[0]?.family || "Raleway",
      }
    );

    store.setCoverData({
      ...coverData,
      cover_meta: {
        ...(coverData.cover_meta || {}),
        spine_title: nextTitle,
      },
    });
  }

  const spineTitlePanel =
    config && store.activeDoc === "cover" ? (
      <SpineTitlePanel
        locale={locale}
        coverMode={String(store.coverData?.cover_meta?.cover_mode || product?.cover_mode || "spread")}
        spineMm={coverSpineMm}
        title={coverSpineTitle}
        fonts={filteredFonts}
        readOnly={readOnly}
        onChange={updateSpineTitle}
      />
    ) : null;

  function guestNameStorageKey() {
    return projectPublicId ? `printapp_guest_name_${projectPublicId}` : null;
  }

  function normalizeGuestName(value: string) {
    return String(value || "").trim().slice(0, 40);
  }

  async function consumeInviteSession(invite: string, displayName?: string) {
    const body: { invite_token: string; display_name?: string } = { invite_token: invite };
    const normalizedName = normalizeGuestName(displayName || "");
    if (normalizedName) body.display_name = normalizedName;

    store.setAuthToken?.(null);
    const inviteSession = await apiPost<InviteSession>("auth/consume-invite", body);
    store.setAuthToken?.(inviteSession.token || null);
    setSessionRole(inviteSession.role || null);
    setInvitedProjectId(Number(inviteSession.project_id || 0) || null);

    // English comment: Keep ?invite in URL so the invite link works after refresh.
    const nextParams = new URLSearchParams(searchParams.toString());
    nextParams.delete("session");
    const nextQuery = nextParams.toString();
    const nextUrl = nextQuery ? `${pathname}?${nextQuery}` : pathname;
    router.replace(nextUrl, { scroll: false });
    setAuthReady(true);
  }

  async function submitGuestName() {
    const invite = pendingInvite;
    if (!invite) return;

    const name = normalizeGuestName(guestNameDraft) || "Guest";
    try {
      const key = guestNameStorageKey();
      if (key) window.localStorage.setItem(key, name);
    } catch {
      // English comment: Local storage may be unavailable in private or restricted browser contexts.
    }

    skippedInviteRef.current = null;
    setGuestNamePromptOpen(false);
    setPendingInvite(null);
    authBootstrapRef.current = false;
    setInviteBootstrapNonce((n) => n + 1);
  }

  async function skipGuestName() {
    const invite = pendingInvite;
    if (!invite) return;

    // English comment: Allow skipping; the next bootstrap pass consumes this invite without display_name.
    skippedInviteRef.current = invite;
    setGuestNamePromptOpen(false);
    setPendingInvite(null);
    authBootstrapRef.current = false;
    setInviteBootstrapNonce((n) => n + 1);
  }

  useEffect(() => {
    if (authBootstrapRef.current) return;
    authBootstrapRef.current = true;

    (async () => {
      try {
        if (sessionToken) {
          setAccessRequired(null);
        }

        if (inviteToken && !sessionToken) {
          const key = guestNameStorageKey();
          let savedName = "";
          try {
            savedName = key ? (window.localStorage.getItem(key) || "") : "";
          } catch {
            savedName = "";
          }

          const nameSkipped = skippedInviteRef.current === inviteToken;
          if (!savedName && !nameSkipped) {
            setPendingInvite(inviteToken);
            setGuestNameDraft("");
            setGuestNamePromptOpen(true);
            return;
          }

          await consumeInviteSession(inviteToken, savedName);
          return;
        }

        if (!sessionToken) {
	          // English comment: If a protected project is opened without a session, show a friendly access gate instead of a hard error.
	          if (projectPublicId && !getEditorAuthToken()) {
	            setError(null);
            setAccessRequired({
              sku,
              projectPublicId,
              message:
                locale === "pl"
                  ? "Wymagany jest bezpieczny link dostępu."
                  : "A secure access link is required."
            });
	            return;
	          }

          store.setAuthToken?.(null);
          setSessionRole(null);
          setAuthReady(true);
          return;
        }

        store.setAuthToken?.(sessionToken);
        setSessionRole(null);

        const nextParams = new URLSearchParams(searchParams.toString());
        nextParams.delete("session");
        const nextQuery = nextParams.toString();
        const nextUrl = nextQuery ? `${pathname}?${nextQuery}` : pathname;
        router.replace(nextUrl, { scroll: false });
        setAuthReady(true);
      } catch (e: any) {
        setError(String(e?.message || e));
      }
    })();
  }, [inviteBootstrapNonce, inviteToken, locale, pathname, projectPublicId, router, searchParams, sessionToken, sku, store]);

  // English comment: Show the lightweight editor guide once for first-time users.
  useEffect(() => {
    if (!onboardingReady) return;
    if (accessRequired) return;
    try {
      if (window.localStorage.getItem(EDITOR_ONBOARDING_STORAGE_KEY) === "done") return;
    } catch {
      return;
    }

    const timer = window.setTimeout(() => {
      setShowOnboardingGuide(true);
    }, 700);

    return () => window.clearTimeout(timer);
  }, [accessRequired, onboardingReady]);

  useEffect(() => {
    if (!editorCanvasReady) return;

    const el = canvasViewportRef.current;
    if (!el) return;

    const measure = () => {
      const r = el.getBoundingClientRect();
      const next = { w: Math.floor(r.width), h: Math.floor(r.height) };
      setCanvasViewport((prev) => (prev.w === next.w && prev.h === next.h ? prev : next));
    };

    measure();
    const frame = window.requestAnimationFrame(measure);

    const ro = new ResizeObserver(() => {
      measure();
    });

    ro.observe(el);

    return () => {
      window.cancelAnimationFrame(frame);
      ro.disconnect();
    };
  }, [editorCanvasReady, fontsReady, store.activeDoc]);

  useEffect(() => {
    if (!editorCanvasReady) return;

    const el = paginationRef.current;
    if (store.activeDoc !== "content" || !el) {
      setPaginationReserve(0);
      return;
    }

    const measure = () => {
      const rect = el.getBoundingClientRect();
      const styles = window.getComputedStyle(el);
      const bottom = Number.parseFloat(styles.bottom || "0");
      const nextReserve = Math.max(0, Math.ceil(rect.height + (Number.isFinite(bottom) ? bottom : 0)));
      setPaginationReserve((prev) => (prev === nextReserve ? prev : nextReserve));
    };

    measure();

    const ro = new ResizeObserver(() => {
      measure();
    });

    ro.observe(el);
    window.addEventListener("resize", measure);

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [editorCanvasReady, store.activeDoc]);

  function centerScrollTo(px: { x: number; y: number }, zoom: number) {
    const el = canvasViewportRef.current;
    if (!el) return;

    const targetX = px.x * zoom - el.clientWidth / 2;
    const targetY = px.y * zoom - el.clientHeight / 2;

    el.scrollLeft = Math.max(0, targetX);
    el.scrollTop = Math.max(0, targetY);
  }

  function centerScrollToCanvas(zoom: number) {
    const el = canvasViewportRef.current;
    if (!el || !stageMetrics) return;

    const totalW = stageMetrics.stageW * zoom;
    const totalH = stageMetrics.stageH * zoom;

    el.scrollLeft = Math.max(0, (totalW - el.clientWidth) / 2);
    el.scrollTop = Math.max(0, (totalH - el.clientHeight) / 2);
  }

  function centerOnSelection() {
    requestAnimationFrame(() => {
      if (selectionCenter) centerScrollTo(selectionCenter, manualZoom);
      else centerScrollToCanvas(manualZoom);
    });
  }

  const handleThumb = useCallback((unitId: string, dataUrl: string) => {
    const pages = Array.isArray(store.contentData?.pages) ? store.contentData.pages : [];
    const idx = pages.findIndex((unit: any, unitIndex: number) => getUnitThumbKey(unit, unitIndex) === unitId);
    if (idx >= 0) {
      thumbImageSignaturesRef.current[unitId] = getUnitImageSignature(pages[idx]);
    }
    setThumbs((prev) => (prev[unitId] === dataUrl ? prev : { ...prev, [unitId]: dataUrl }));
  }, [store.contentData]);

  const handlePreloadedThumb = useCallback(({ unitIndex, dataUrl }: { unitIndex: number; dataUrl: string }) => {
    const unit = Array.isArray(store.contentData?.pages) ? store.contentData.pages[unitIndex] : null;
    const key = getUnitThumbKey(unit, unitIndex);
    thumbImageSignaturesRef.current[key] = getUnitImageSignature(unit);
    handleThumb(key, dataUrl);
    setThumbPreloadQueue((q) => q.filter((idx) => idx !== unitIndex));
  }, [handleThumb, store.contentData]);

  // English comment: Build background thumbnail queue for content units that already have content.
  useEffect(() => {
    if (!fontsReady) return;
    if (!store.contentData) return;

    const pages = Array.isArray(store.contentData.pages) ? store.contentData.pages : [];
    const missing: number[] = [];

    pages.forEach((unit: any, idx: number) => {
      if (!unitHasMeaningfulContent(unit)) return;
      const key = getUnitThumbKey(unit, idx);
      const imageSignature = getUnitImageSignature(unit);
      const hasStaleImageThumb = Boolean(imageSignature) && thumbImageSignaturesRef.current[key] !== imageSignature;
      if (!thumbs[key] || hasStaleImageThumb) {
        missing.push(idx);
      }
    });

    if (missing.length === 0) return;

    setThumbPreloadQueue((prev) => {
      let changed = false;
      const merged = [...prev];
      for (const idx of missing) {
        if (!merged.includes(idx)) {
          merged.push(idx);
          changed = true;
        }
      }
      return changed ? merged : prev;
    });
  }, [fontsReady, store.contentData, thumbs]);

  // English comment: Remove thumbnails and queued jobs for units that no longer contain meaningful content.
  useEffect(() => {
    if (!store.contentData) return;

    const pages = Array.isArray(store.contentData.pages) ? store.contentData.pages : [];
    setThumbPreloadQueue((prev) => {
      const next = prev.filter((idx) => unitHasMeaningfulContent(pages[idx]));
      return next.length === prev.length ? prev : next;
    });

    setThumbs((prev) => {
      let changed = false;
      const next = { ...prev };
      pages.forEach((unit: any, idx: number) => {
        if (unitHasMeaningfulContent(unit)) return;
        const key = getUnitThumbKey(unit, idx);
        if (next[key]) {
          delete next[key];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [store.contentData]);

  function openImagesPopup() {
    setPropsDrawerOpen(false);
    setShowImages(true);
  }

  function requestImageLibraryRefresh() {
    setImageLibraryRefreshNonce((value) => value + 1);
  }

  const refreshGeneratedThemeTemplates = useCallback(async () => {
    if (!store.authToken) {
      setGeneratedThemeTemplates([]);
      return;
    }

    try {
      const res = await apiGet<{ items: ThemeTemplate[] }>("templates/themes");
      setGeneratedThemeTemplates(Array.isArray(res.items) ? res.items : []);
    } catch {
      // English comment: Dynamic templates are optional; keep static templates usable if loading fails.
      setGeneratedThemeTemplates([]);
    }
  }, [store.authToken]);

  useEffect(() => {
    refreshGeneratedThemeTemplates();
  }, [refreshGeneratedThemeTemplates]);

  function getContentTemplateContext() {
    if (!store.contentData || !product || !config) return null;

    const formatKey = String(store.contentData.meta?.format_key || product.format_key || "a4_portrait");
    const fmt = config.formats.find((f: any) => f.id === formatKey);
    const contentLayoutRaw = String(store.contentData.meta?.content_layout || product.content_layout || "book");
    const contentLayout: "book" | "spreads_only" = contentLayoutRaw === "spreads_only" ? "spreads_only" : "book";
    const bleedMm = Number(store.contentData.meta?.bleed_mm ?? product.bleed_mm ?? fmt?.bleed_mm ?? 3);
    const safeMm = Number(store.contentData.meta?.safe_mm ?? product.safe_mm ?? fmt?.safe_mm ?? 5);
    // English comment: Use explicit page_margins_mm if present; otherwise fallback to safeMm on all sides.
    const m = store.contentData.meta?.page_margins_mm || product.page_margins_mm || {};
    const margins = templateMarginsFromMeta(m, safeMm);

    return {
      pageWmm: Number(fmt?.page_mm?.width ?? 210),
      pageHmm: Number(fmt?.page_mm?.height ?? 297),
      contentLayout,
      bleedMm,
      margins,
      formatGroup: formatKeyToGroup(formatKey),
    };
  }

  function applyThemeById(themeId: string) {
    if (readOnly) return;
    const ctx = getContentTemplateContext();
    if (!ctx || !store.contentData || !product) {
      showToast(locale === "pl" ? "Nie można zastosować szablonu." : "Cannot apply template.", "error");
      return;
    }

    const theme = allThemeTemplates.find((t) => t.id === themeId);
    if (!theme) return;

    // English comment: Save one-level undo snapshot before replacing content with a theme.
    themeUndoRef.current = {
      contentData: deepClone(store.contentData),
      contentUnitIndex: store.contentUnitIndex,
    };
    setCanUndoTheme(true);

    const next = applyThemeTemplate({
      docData: store.contentData,
      theme,
      pageWmm: ctx.pageWmm,
      pageHmm: ctx.pageHmm,
      contentLayout: ctx.contentLayout,
      minPages: Number(product.min_pages ?? 24),
      maxPages: Number(product.max_pages ?? 190),
      bleedMm: ctx.bleedMm,
      margins: ctx.margins,
      textPresets: projectSettings?.text_presets || {},
      imageDefaults: projectSettings?.image_defaults || {}
    });

    store.setContentData(next);
    store.setActiveDoc("content");
    store.setContentUnitIndex(0);
    store.selectElement("content", null);
    showToast(locale === "pl" ? "Motyw zastosowany." : "Theme applied.", "ok");
  }

  // English comment: Undo the last theme application (one level).
  function undoLastTheme() {
    if (readOnly) return;
    const u = themeUndoRef.current;
    if (!u) return;

    store.setContentData(u.contentData);
    store.setActiveDoc("content");
    store.setContentUnitIndex(u.contentUnitIndex ?? 0);
    store.selectElement("content", null);

    themeUndoRef.current = null;
    setCanUndoTheme(false);

    showToast(locale === "pl" ? "Cofnięto motyw." : "Undone theme changes.", "ok");
  }

  async function createThemeTemplateFromCurrentProject(input: { name: string; tipsText?: string }) {
    if (sessionRole !== "admin") {
      showToast(locale === "pl" ? "Tylko administrator może tworzyć motywy." : "Only an administrator can create themes.", "error");
      return;
    }
    if (!store.contentData) {
      showToast(locale === "pl" ? "Brak projektu do zapisania jako motyw." : "No project content to save as a theme.", "error");
      return;
    }

    const cleanName = String(input.name || "").trim().replace(/\s+/g, " ").slice(0, 80);
    if (!cleanName) {
      showToast(locale === "pl" ? "Podaj nazwę motywu." : "Enter a theme name.", "error");
      return;
    }

    try {
      setThemeTemplateCreateBusy(true);
      const template = buildGeneratedThemeTemplate({
        name: cleanName,
        contentData: store.contentData,
        formatGroup: activeFormatGroup,
        tipsText: input.tipsText
      });
      const res = await apiPost<{ template: ThemeTemplate }>("templates/themes", {
        name: cleanName,
        format_group: activeFormatGroup,
        source_project_id: store.projectId,
        template
      });

      setGeneratedThemeTemplates((prev) => {
        const next = res.template;
        if (!next?.id) return prev;
        return [next, ...prev.filter((item) => item.id !== next.id)];
      });
      showToast(locale === "pl" ? "Motyw został utworzony." : "Theme created.", "ok");
    } catch (e: unknown) {
      showToast(e instanceof Error ? e.message : String(e), "error");
    } finally {
      setThemeTemplateCreateBusy(false);
    }
  }

  async function deleteGeneratedThemeTemplate(themeId: string) {
    if (sessionRole !== "admin") {
      showToast(locale === "pl" ? "Tylko administrator może usuwać motywy." : "Only an administrator can delete themes.", "error");
      return;
    }

    const theme = generatedThemeTemplates.find((item) => item.id === themeId);
    if (!theme || theme.source !== "generated") {
      showToast(locale === "pl" ? "Można usuwać tylko wygenerowane motywy." : "Only generated themes can be deleted.", "error");
      return;
    }

    const confirmed =
      typeof window === "undefined" ||
      window.confirm(
        locale === "pl"
          ? `Usunąć motyw "${theme.name}"? Tej operacji nie można cofnąć.`
          : `Delete theme "${theme.name}"? This cannot be undone.`
      );
    if (!confirmed) return;

    try {
      setThemeTemplateDeleteBusyId(themeId);
      const res = await apiDelete<{ ok: boolean; id?: string }>(`templates/themes/${encodeURIComponent(themeId)}`);
      const deletedId = res.id || themeId;
      setGeneratedThemeTemplates((prev) => prev.filter((item) => item.id !== deletedId));
      showToast(locale === "pl" ? "Motyw został usunięty." : "Theme deleted.", "ok");
    } catch (e: unknown) {
      showToast(e instanceof Error ? e.message : String(e), "error");
    } finally {
      setThemeTemplateDeleteBusyId(null);
    }
  }

  function insertSpreadTemplateById(templateId: string) {
    if (readOnly) return;
    const ctx = getContentTemplateContext();
    if (!ctx || !store.contentData) {
      showToast(locale === "pl" ? "Nie można dodać rozkładówki." : "Cannot insert spread.", "error");
      return;
    }

    const template = getSpreadTemplates(ctx.formatGroup).find((t) => t.id === templateId);
    if (!template) return;

    const pages = Array.isArray(store.contentData.pages) ? store.contentData.pages : [];
    const insertIndex = ctx.contentLayout === "book" && pages.length >= 2 ? pages.length - 1 : pages.length;
    const next = insertSpreadTemplateAtEnd({
      docData: store.contentData,
      template,
      pageWmm: ctx.pageWmm,
      pageHmm: ctx.pageHmm,
      contentLayout: ctx.contentLayout,
      bleedMm: ctx.bleedMm,
      margins: ctx.margins,
      textPresets: projectSettings?.text_presets || {},
      imageDefaults: projectSettings?.image_defaults || {}
    });

    store.setContentData(next);
    store.setActiveDoc("content");
    store.setContentUnitIndex(insertIndex);
    store.selectElement("content", null);
    showToast(locale === "pl" ? "Rozkładówka dodana." : "Spread inserted.", "ok");
  }

  function openBackgroundToolPanel() {
    const action: HelpAction = { type: "open_tool", tool: "background" };
    setShowImages(false);
    setPropsDrawerOpen(false);
    setHelpAction(action);
    setHelpActionNonce((n) => n + 1);
    setMobileToolAction(action);
    setMobileToolActionNonce((n) => n + 1);
  }

  // ------------- Bootstrap: config + project + drafts -------------
  useEffect(() => {
    // English comment: Backgrounds use the dedicated background tool, not the generic properties drawer.
    if (readOnly) return;
    const selectedId = store.selected?.elementId;
    if (!selectedId) return;

    const selectedDoc = store.selected.doc;
    const docData = selectedDoc === "content" ? store.contentData : store.coverData;
    const found = docData ? findElementById(docData, selectedId) : null;
    const isBackground = found?.el?.type === "background" || Boolean(found?.el?.is_background);

    if (isBackground) {
      setToolsCollapsed(true);
      openBackgroundToolPanel();
      return;
    }

    setToolsCollapsed(true);
    setShowImages(false);
    setPropsDrawerOpen(true);
  }, [readOnly, store.selected?.doc, store.selected?.elementId]);

  useEffect(() => {
    if (!readOnly) return;
    setPropsDrawerOpen(false);
    setShowImages(false);
  }, [readOnly]);

  useEffect(() => {
    if (!stageMetrics?.stageW || !stageMetrics?.stageH) return;

    const padX = isActiveContentSpread ? 0 : 20;
    const padY = 0;
    const fitViewportHeight = Math.max(100, canvasViewport.h - canvasBottomReserve);
    const availW = Math.max(100, canvasViewport.w - 2 * padX);
    const availH = Math.max(100, fitViewportHeight - 2 * padY);
    const z = clamp(Math.min(availW / stageMetrics.stageW, availH / stageMetrics.stageH), 0.1, 4);

    setFitZoom(z);

    // Keep slider synced to fit until the user changes zoom manually.
    if (!zoomTouched) {
      setManualZoom(z);
      requestAnimationFrame(() => centerScrollToCanvas(z));
    }
  }, [canvasBottomReserve, canvasViewport.h, canvasViewport.w, isActiveContentSpread, stageMetrics?.stageH, stageMetrics?.stageW, zoomTouched]);

  useEffect(() => {
    if (!zoomTouched || !stageMetrics?.stageW || !stageMetrics?.stageH) return;
    requestAnimationFrame(() => centerScrollToCanvas(manualZoom));
  }, [manualZoom, stageMetrics?.stageH, stageMetrics?.stageW, zoomTouched]);

  useEffect(() => {
    (async () => {
      // Wait for browser font loading API to avoid fallback rendering on first paint.
      // @ts-ignore
      if (document?.fonts?.ready) {
        // @ts-ignore
        await document.fonts.ready;
      }
      setFontsReady(true);
    })();
  }, []);

  function clearProjectImagePreloadTimer() {
    if (projectImagePreloadTimerRef.current == null) return;
    window.clearInterval(projectImagePreloadTimerRef.current);
    projectImagePreloadTimerRef.current = null;
  }

  function setProjectImagePreloadPercent(percent: number, label?: string) {
    setProjectImagePreload({
      running: true,
      total: 100,
      done: Math.round(clamp(percent, 0, 100)),
      label
    });
  }

  function animateProjectImagePreloadProgress(from: number, to: number, label?: string) {
    clearProjectImagePreloadTimer();
    let progress = clamp(from, 0, to);
    setProjectImagePreloadPercent(progress, label);

    projectImagePreloadTimerRef.current = window.setInterval(() => {
      progress = Math.min(to, progress + Math.max(0.7, (to - progress) * 0.09));
      setProjectImagePreloadPercent(progress, label);
      if (progress >= to - 0.2) clearProjectImagePreloadTimer();
    }, 140);
  }

  function delay(ms: number) {
    return new Promise((resolve) => window.setTimeout(resolve, ms));
  }

  useEffect(() => {
    if (!authReady) return;

    let cancelled = false;

    (async () => {
      try {
        setError(null);

        // 1) Load editor-config
        const cfg = await apiGet<EditorConfig>("editor-config");
        if (cancelled) return;
        setConfig(cfg);

        // 1b) Load product config by SKU (manager-driven)
        const prod = await apiGet<ProductConfig>(`products/by-sku/${encodeURIComponent(sku)}`);
        if (cancelled) return;
        setProduct(prod);

        // 2) Resolve or create project
        let prj: Project;
        const authToken = getEditorAuthToken();

        if (projectPublicId || invitedProjectId) {
	          if (!authToken) {
	            if (projectPublicId) {
	              setError(null);
              setAccessRequired({
                sku,
                projectPublicId,
                message:
                  locale === "pl"
                    ? "Wymagany jest bezpieczny link dostępu."
                    : "A secure access link is required."
              });
	              return;
	            }
            setError("Ten projekt można otworzyć tylko z Twojego konta w sklepie (wymagane logowanie).");
            return;
          }

          try {
            const sess = await apiGet<ProjectSession>("auth/session");
            setSessionRole(sess.role || null);
            const sessionProjectId = Number(sess.project_id || invitedProjectId);
            if (!sessionProjectId) {
              throw new Error("Invite session is missing project_id.");
            }
            const projectRef = await apiGet<Project>(`projects/${sessionProjectId}/public-id`);

            if (projectPublicId && projectRef.public_id !== projectPublicId) {
              throw new Error("Project session does not match requested project.");
            }

            prj = {
              id: projectRef.id,
              public_id: projectRef.public_id,
              name: projectRef.name,
              created_at: projectRef.created_at,
            };
          } catch (e: any) {
            const msg = String(e?.message || e);
	            if (/\bfailed:\s*(401|403)\b/.test(msg)) {
	              if (projectPublicId) {
	                setError(null);
                setAccessRequired({
                  sku,
                  projectPublicId,
                  message:
                    locale === "pl"
                      ? "Wymagany jest bezpieczny link dostępu."
                      : "A secure access link is required."
                });
	                return;
	              }
              throw new Error("Ten projekt można otworzyć tylko z Twojego konta w sklepie (wymagane logowanie).");
            }
            throw e;
          }
        } else {
          if (createdProjectRef.current) {
            prj = createdProjectRef.current;
          } else {
            prj = await apiPost<Project>("projects", { name: `editor:${sku}:${Date.now()}`, sku });
            if (prj?.public_id && prj?.claim_token) {
              persistProjectClaimToken(prj.public_id, prj.claim_token);
            }
            createdProjectRef.current = prj;
          }
        }

        if (cancelled) return;
        setProjectState(prj);

        const canAutoIssueSession = !projectPublicId && !invitedProjectId;
        if (canAutoIssueSession && !getEditorAuthToken()) {
          const issued = await issueProjectSession({ project_id: prj.id, project_public_id: prj.public_id });
          setSessionRole(String(issued?.role || "user"));
        }

        let settingsRes: { settings: any };
        let cd: DraftRow | null;
        let cvd: DraftRow | null;

        try {
          // Load per-project settings (text presets, etc.)
          settingsRes = await apiGet<{ settings: any }>(`projects/${prj.id}/settings`);
          cd = await apiGet<DraftRow | null>(`projects/${prj.id}/draft`);
          cvd = await apiGet<DraftRow | null>(`projects/${prj.id}/cover-draft`);
        } catch (e: any) {
          const msg = String(e?.message || e);
          const isAuthError = /\bfailed:\s*(401|403)\b/.test(msg);

          if (!canAutoIssueSession || !isAuthError) {
            throw e;
          }

          const issued = await issueProjectSession({ project_id: prj.id, project_public_id: prj.public_id });
          setSessionRole(String(issued?.role || "user"));
          settingsRes = await apiGet<{ settings: any }>(`projects/${prj.id}/settings`);
          cd = await apiGet<DraftRow | null>(`projects/${prj.id}/draft`);
          cvd = await apiGet<DraftRow | null>(`projects/${prj.id}/cover-draft`);
        }

        if (cancelled) return;
        const settings = settingsRes.settings || {};
        setProjectSettings(settings);
        imagePreloadProjectIdRef.current = prj.id;
        initialImagePreloadDoneRef.current = false;
        preloadedImageUrlsRef.current = new Set();
        store.setProject({ id: prj.id, public_id: prj.public_id, sku });

        // 4) Initialize contentData / coverData
        // If drafts exist, use them; otherwise initialize from product config
        let contentData = cd?.data_json ?? createDefaultContentDataFromProduct(prod);
        const coverData = cvd?.data_json ?? defaultCoverDataFromProduct(prod);
        const minPages = Number(prod.min_pages ?? 24);
        const fmt = cfg.formats.find((f: any) => f.id === prod.format_key);
        const pageWmm = Number(fmt?.page_mm?.width ?? 210);
        const pageHmm = Number(fmt?.page_mm?.height ?? 297);
        const prodLayout = normalizeContentLayout(prod.content_layout);

        if (!contentData.meta) {
          contentData.meta = createDefaultContentDataFromProduct(prod).meta;
        }
        contentData.meta = {
          ...(contentData.meta || {}),
          content_layout: prodLayout,
          format_key: prod.format_key,
          bleed_mm: Number(prod.bleed_mm ?? 3),
          safe_mm: Number(prod.safe_mm ?? 5),
          page_mm: { width: pageWmm, height: pageHmm },
          page_margins_mm: normalizePageMarginsMm(contentData.meta?.page_margins_mm),
        };

        if (String(contentData.meta.layout_mode || "pages") !== "book_spreads") {
          contentData = migratePagesToBookSpreadsUnits(contentData, pageWmm, prodLayout);
        } else {
          contentData.meta = {
            ...(contentData.meta || {}),
            layout_mode: "book_spreads",
            content_layout: prodLayout,
            page_margins_mm: normalizePageMarginsMm(contentData.meta?.page_margins_mm),
          };
        }

        let contentDataFixed = ensureUnitIds(ensureMinPhysicalPages(contentData, minPages));

        // English comment: Auto-apply theme from URL only once for new/empty projects.
        if (themeId) {
          const alreadyAppliedTheme = String(contentDataFixed.meta?.applied_theme_id || "");
          let theme = THEME_TEMPLATES.find((item) => item.id === themeId);
          if (!theme) {
            const res = await apiGet<{ items: ThemeTemplate[] }>("templates/themes");
            const items = Array.isArray(res.items) ? res.items : [];
            setGeneratedThemeTemplates(items);
            theme = items.find((item) => item.id === themeId);
          }
          if (cancelled) return;

          const themeFormatMatches =
            !theme?.format_group || theme.format_group === formatKeyToGroup(String(prod.format_key || ""));
          if (!alreadyAppliedTheme && theme && themeFormatMatches && isEmptyContentDoc(contentDataFixed)) {
            contentDataFixed = applyThemeTemplate({
              docData: contentDataFixed,
              theme,
              pageWmm,
              pageHmm,
              contentLayout: prodLayout,
              minPages,
              maxPages: Number(prod.max_pages ?? 190),
              bleedMm: Number(contentDataFixed.meta?.bleed_mm ?? prod.bleed_mm ?? fmt?.bleed_mm ?? 3),
              margins: templateMarginsFromMeta(
                contentDataFixed.meta?.page_margins_mm,
                Number(contentDataFixed.meta?.safe_mm ?? prod.safe_mm ?? fmt?.safe_mm ?? 5)
              ),
              textPresets: settings?.text_presets || {},
              imageDefaults: settings?.image_defaults || {}
            });
            contentDataFixed = {
              ...contentDataFixed,
              meta: {
                ...(contentDataFixed.meta || {}),
                applied_theme_id: themeId
              }
            };

            const nextParams = new URLSearchParams(searchParams.toString());
            nextParams.delete("theme");
            const nextQuery = nextParams.toString();
            const nextUrl = nextQuery ? `${pathname}?${nextQuery}` : pathname;
            router.replace(nextUrl, { scroll: false });
          }
        }

        // Ensure required cover_meta exists even for older drafts
        if (!coverData.cover_meta) {
          coverData.cover_meta = defaultCoverDataFromProduct(prod).cover_meta;
        }
        coverData.cover_meta.format_key = prod.format_key;
        coverData.cover_meta.bleed_mm = Number(prod.bleed_mm ?? 3);
        coverData.cover_meta.safe_mm = Number(prod.safe_mm ?? 5);
        coverData.cover_meta.cover_mode = prod.cover_mode || "spread";
        coverData.cover_meta.spine_mm = resolveProductSpineMm(prod, getContentPhysicalPageCount(contentDataFixed));
        Object.assign(coverData.cover_meta, resolveProductCoverGeometryMeta(prod));

        // If product requires separate cover, ensure 2 pages exist
        if (coverData.cover_meta.cover_mode === "separate" && (!Array.isArray(coverData.pages) || coverData.pages.length !== 2)) {
          coverData.pages = [{ panel: "back", elements: [] }, { panel: "front", elements: [] }];
        }

        // If product is spread, ensure at least 1 page
        if (coverData.cover_meta.cover_mode === "spread" && (!Array.isArray(coverData.pages) || coverData.pages.length < 1)) {
          coverData.pages = [{ panel: "spread", elements: [] }];
        }

        const normalizedContent = ensureElementIds(contentDataFixed);
        const normalizedCover = ensureElementIds(coverData);
        const initialImageAssetIds = new Set<number>([
          ...collectAssetIds(normalizedContent),
          ...collectAssetIds(normalizedCover)
        ]);
        const initialPreviewUrls = collectImagePreviewUrls(normalizedContent, normalizedCover);
        const hasInitialImages = initialImageAssetIds.size > 0 || initialPreviewUrls.length > 0;
        const initialPreloadStartedAt = Date.now();
        const initialPreloadRunId = ++projectImagePreloadRunRef.current;

        if (hasInitialImages) {
          animateProjectImagePreloadProgress(
            3,
            32,
            locale === "pl" ? "Przygotowanie zdjęć" : "Preparing images"
          );
        }

        const hydratedContent = ensureUnitIds(await hydrateDocImageUrls(normalizedContent));
        const hydratedCover = await hydrateDocImageUrls(normalizedCover);

        let finalPreloadTotal = 0;
        if (hasInitialImages) {
          const hydratedUrls = collectImagePreviewUrls(hydratedContent, hydratedCover);
          finalPreloadTotal = Math.max(1, hydratedUrls.length || initialPreviewUrls.length || initialImageAssetIds.size);
          if (hydratedUrls.length > 0) {
            clearProjectImagePreloadTimer();
            let visualImageProgress = 36;
            let loadedImageCount = 0;
            let loadedImageTotal = hydratedUrls.length;
            const imageLoadingLabel = () => {
              if (loadedImageCount <= 0) {
                return locale === "pl" ? "Ładowanie zdjęć" : "Loading images";
              }

              return locale === "pl"
                ? `Ładowanie zdjęć ${loadedImageCount}/${loadedImageTotal}`
                : `Loading images ${loadedImageCount}/${loadedImageTotal}`;
            };
            setProjectImagePreloadPercent(
              visualImageProgress,
              imageLoadingLabel()
            );

            projectImagePreloadTimerRef.current = window.setInterval(() => {
              visualImageProgress = Math.min(88, visualImageProgress + Math.max(0.4, (88 - visualImageProgress) * 0.045));
              setProjectImagePreloadPercent(visualImageProgress, imageLoadingLabel());
              if (visualImageProgress >= 87.8) clearProjectImagePreloadTimer();
            }, 160);

            await preloadCanvasImageUrls(hydratedUrls, (done, total) => {
              if (cancelled || projectImagePreloadRunRef.current !== initialPreloadRunId) return;
              loadedImageCount = done;
              loadedImageTotal = total;
              const percent = 36 + (clamp(done / Math.max(1, total), 0, 1) * 56);
              visualImageProgress = Math.max(visualImageProgress, percent);
              setProjectImagePreloadPercent(
                visualImageProgress,
                imageLoadingLabel()
              );
            });
            clearProjectImagePreloadTimer();
            hydratedUrls.forEach((url) => preloadedImageUrlsRef.current.add(url));
          } else {
            animateProjectImagePreloadProgress(
              36,
              84,
              locale === "pl" ? "Sprawdzanie projektu" : "Checking project"
            );
            await delay(240);
          }

          if (cancelled) return;
          initialImagePreloadDoneRef.current = true;
          clearProjectImagePreloadTimer();
          setProjectImagePreloadPercent(94, locale === "pl" ? "Finalizacja" : "Finalizing");
        }

        store.setContentData(hydratedContent);
        store.setCoverData(hydratedCover);

        if (hasInitialImages) {
          const elapsedMs = Date.now() - initialPreloadStartedAt;
          const remainingMs = Math.max(0, 900 - elapsedMs);
          if (remainingMs > 0) await delay(remainingMs);
          if (cancelled) return;
          setProjectImagePreloadPercent(100, locale === "pl" ? "Gotowe" : "Ready");
          await delay(160);
          if (cancelled) return;
          setProjectImagePreload({ running: false, total: finalPreloadTotal, done: finalPreloadTotal });
        }
      } catch (e: any) {
        if (cancelled) return;
        clearProjectImagePreloadTimer();
        setProjectImagePreload({ running: false, total: 0, done: 0 });
        setError(String(e?.message || e));
      }
    })();

    return () => {
      cancelled = true;
      clearProjectImagePreloadTimer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authReady, invitedProjectId, sku, projectPublicId, sessionToken]);

  // English comment: Keep cover spine and derived spine title size in sync with actual content page count.
  useEffect(() => {
    if (!product || !store.contentData || !store.coverData) return;
    const nextCover = getSyncedCoverDataForPersist(store.coverData, store.contentData);
    if (nextCover === store.coverData) return;
    store.setCoverData(nextCover);
  }, [product, config, filteredFonts, store.contentData, store.coverData]);

  // ------------- Presence + soft edit lock -------------
  useEffect(() => {
    if (!store.projectId) return;
    if (!store.authToken) return;

    let stopped = false;
    setCanEditNow(false);
    setLock(null);

    async function tick() {
      const projectId = store.projectId;
      if (!projectId) return;

      try {
        await apiPost(`projects/${projectId}/presence/heartbeat`, {});

        const p = await apiGet<PresenceRow[]>(`projects/${projectId}/presence`);
        if (!stopped) setPresence(uniqPresenceByLabel(Array.isArray(p) ? p : []));

        const role = sessionRole || "";
        const canClaimLock = role === "admin" || role === "user" || role === "guest_editor";
        if (canClaimLock) {
          try {
            await apiPost(`projects/${projectId}/lock/claim`, {});
          } catch {
            // English comment: 423 means another collaborator holds the lock; status is fetched below.
          }
        }

        const st = await apiGet<ProjectLockState>(`projects/${projectId}/lock`);
        if (!stopped) {
          setLock(st);
          setCanEditNow(Boolean(canClaimLock && st?.locked && st?.mine));
        }
      } catch {
        // English comment: Presence and lock polling are best-effort UI hints.
        if (!stopped) setCanEditNow(false);
      }
    }

    tick();
    const timer = window.setInterval(tick, 10000);
    return () => {
      stopped = true;
      window.clearInterval(timer);
    };
  }, [store.projectId, store.authToken, sessionRole]);

  // English comment: In read-only mode, auto-refresh drafts so viewers see updates.
  useEffect(() => {
    if (!store.projectId) return;
    if (!store.authToken) return;
    if (!readOnly) return;

    let stopped = false;

    async function refreshDrafts() {
      try {
        const projectId = store.projectId;
        if (!projectId) return;

        const contentRow = await apiGet<any>(`projects/${projectId}/draft`);
        const coverRow = await apiGet<any>(`projects/${projectId}/cover-draft`);
        if (stopped) return;

        // English comment: API returns DB row; snapshot JSON is in data_json.
        const nextContent = contentRow?.data_json || null;
        const nextCover = coverRow?.data_json || null;
        if (nextContent) store.setContentData(nextContent);
        if (nextCover) store.setCoverData(nextCover);
      } catch {
        // English comment: Ignore transient refresh errors.
      }
    }

    refreshDrafts();
    const timer = window.setInterval(refreshDrafts, 10000);
    return () => {
      stopped = true;
      window.clearInterval(timer);
    };
  }, [store.projectId, store.authToken, readOnly]);

  // ------------- Autosave: every 15 seconds -------------
  useEffect(() => {
    let cancelled = false;

    const interval = setInterval(() => {
      (async () => {
        try {
          if (cancelled || saveAsPausedRef.current) return;
          setSaveError(null);

          const projectId = store.projectId;
          const contentData = store.contentData;
          const coverData = store.coverData;

          if (!projectId) return;
          if (readOnly) return;

          // Save content draft if present
          if (contentData) {
            await apiPut(`projects/${projectId}/draft`, { data: stripPreviewFields(store.contentData) });
          }

          // Save cover draft if present
          if (coverData) {
            const coverDataForSave = getSyncedCoverDataForPersist(coverData, contentData);
            if (coverDataForSave !== coverData) store.setCoverData(coverDataForSave);
            await apiPut(`projects/${projectId}/cover-draft`, { data: stripPreviewFields(coverDataForSave) });
          }

          if (!cancelled) {
            setLastSaveAt(new Date().toLocaleTimeString());
          }
        } catch (e: any) {
          if (!cancelled) {
            setSaveError(String(e?.message || e));
          }
        }
      })();
    }, 15000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [store, readOnly]);

  function getCenteredImageInsertPosition(
    targetDoc: "content" | "cover",
    targetUnitIndex: number,
    wMm: number,
    hMm: number
  ) {
    const docData = targetDoc === "content" ? store.contentData : store.coverData;
    const fallback = { x_mm: 20, y_mm: 120 };
    if (!docData || !config) return fallback;

    if (targetDoc === "content") {
      const formatKey = String(docData?.meta?.format_key || product?.format_key || "a4_portrait");
      const fmt = config.formats.find((f: any) => f.id === formatKey);
      const pageWmm = Number(fmt?.page_mm?.width ?? 210);
      const pageHmm = Number(fmt?.page_mm?.height ?? 297);
      const unit = Array.isArray(docData?.pages) ? docData.pages[targetUnitIndex] : null;
      const isSpread =
        String(docData?.meta?.layout_mode || "pages") === "book_spreads" &&
        String(unit?.kind || "single") === "spread";

      return centerPosMm(isSpread ? pageWmm * 2 : pageWmm, pageHmm, wMm, hMm);
    }

    const formatKey = String(docData?.cover_meta?.format_key || product?.format_key || "a4_portrait");
    const fmt = config.formats.find((f: any) => f.id === formatKey);
    const pageWmm = Number(fmt?.page_mm?.width ?? 210);
    const pageHmm = Number(fmt?.page_mm?.height ?? 297);
    const coverMode = String(docData?.cover_meta?.cover_mode || product?.cover_mode || "spread");
    const spineMm = Number(
      docData?.cover_meta?.spine_mm ?? resolveProductSpineMm(product, getContentPhysicalPageCount(store.contentData))
    );
    const coverGeometry = resolveCoverSpreadGeometry({
      panelWmm: pageWmm,
      panelHmm: pageHmm,
      spineMm,
      coverGeometryType: docData?.cover_meta?.cover_geometry_type,
      coverWrapMm: docData?.cover_meta?.cover_wrap_mm,
      coverSqueezeMm: docData?.cover_meta?.cover_squeeze_mm,
      coverOverlapMm: docData?.cover_meta?.cover_overlap_mm,
    });
    const artWmm = coverMode === "spread" ? coverGeometry.artWmm : pageWmm;
    const artHmm = coverMode === "spread" ? coverGeometry.artHmm : pageHmm;

    return centerPosMm(artWmm, artHmm, wMm, hMm);
  }

  function getArtboardBoundsMm(targetDoc: "content" | "cover", targetUnitIndex: number) {
    const docData = targetDoc === "content" ? store.contentData : store.coverData;
    const fallback = { x_mm: 0, y_mm: 0, w_mm: 210, h_mm: 297, bleed_mm: 3 };
    if (!docData || !config) return fallback;

    if (targetDoc === "content") {
      const formatKey = String(docData?.meta?.format_key || product?.format_key || "a4_portrait");
      const fmt = config.formats.find((f: any) => f.id === formatKey);
      const pageWmm = Number(fmt?.page_mm?.width ?? 210);
      const pageHmm = Number(fmt?.page_mm?.height ?? 297);
      const bleedMm = Number(docData?.meta?.bleed_mm ?? product?.bleed_mm ?? 3);
      const unit = Array.isArray(docData?.pages) ? docData.pages[targetUnitIndex] : null;
      const isSpread =
        String(docData?.meta?.layout_mode || "pages") === "book_spreads" &&
        String(unit?.kind || "single") === "spread";
      const artWmm = isSpread ? pageWmm * 2 : pageWmm;

      return {
        x_mm: -bleedMm,
        y_mm: -bleedMm,
        w_mm: artWmm + 2 * bleedMm,
        h_mm: pageHmm + 2 * bleedMm,
        bleed_mm: bleedMm
      };
    }

    const formatKey = String(docData?.cover_meta?.format_key || product?.format_key || "a4_portrait");
    const fmt = config.formats.find((f: any) => f.id === formatKey);
    const pageWmm = Number(fmt?.page_mm?.width ?? 210);
    const pageHmm = Number(fmt?.page_mm?.height ?? 297);
    const bleedMm = Number(docData?.cover_meta?.bleed_mm ?? product?.bleed_mm ?? 3);
    const coverMode = String(docData?.cover_meta?.cover_mode || product?.cover_mode || "spread");
    const spineMm = Number(
      docData?.cover_meta?.spine_mm ?? resolveProductSpineMm(product, getContentPhysicalPageCount(store.contentData))
    );
    const coverGeometry = resolveCoverSpreadGeometry({
      panelWmm: pageWmm,
      panelHmm: pageHmm,
      spineMm,
      coverGeometryType: docData?.cover_meta?.cover_geometry_type,
      coverWrapMm: docData?.cover_meta?.cover_wrap_mm,
      coverSqueezeMm: docData?.cover_meta?.cover_squeeze_mm,
      coverOverlapMm: docData?.cover_meta?.cover_overlap_mm,
    });
    const artWmm = coverMode === "spread" ? coverGeometry.artWmm : pageWmm;
    const artHmm = coverMode === "spread" ? coverGeometry.artHmm : pageHmm;

    return {
      x_mm: -bleedMm,
      y_mm: -bleedMm,
      w_mm: artWmm + 2 * bleedMm,
      h_mm: artHmm + 2 * bleedMm,
      bleed_mm: bleedMm
    };
  }

  // ------------- UI actions -------------
  /**
   * Add a text element to the active doc/page.
   */
  function getTextPreset(role: "heading" | "body" | "list") {
    const preset = projectSettings?.text_presets?.[role];
    if (preset && preset.style) return preset;

    const allowed = new Set(product?.allowed_fonts || []);
    const availableFonts = Array.isArray(config?.fonts)
      ? config.fonts.filter((font: any) => allowed.size === 0 || allowed.has(font.family))
      : [];
    const preferredRole = role === "heading" ? "heading" : "text";
    const baseFont =
      availableFonts.find((font: any) => font.role === preferredRole) ||
      availableFonts[0] ||
      config?.fonts?.[0] ||
      { family: "Raleway", variants: { bold: true } };
    const baseFamily = String(baseFont.family || "Raleway");
    const baseWeight = baseFont.variants?.bold ? 700 : 400;

    if (role === "heading") {
      return {
        style: {
          font_family: baseFamily,
          font_size_pt: 48,
          font_weight: baseWeight,
          color_hex: "#000000",
          text_align: "left",
          line_height: 1.1,
          padding_mm: 2
        },
        box_mm: { w: 170, h: 24 }
      };
    }

    if (role === "list") {
      return {
        style: {
          font_family: baseFamily,
          font_size_pt: 14,
          font_weight: 400,
          color_hex: "#000000",
          text_align: "left",
          line_height: 1.4,
          padding_mm: 2
        },
        box_mm: { w: 170, h: 60 }
      };
    }

    return {
      style: {
        font_family: baseFamily,
        font_size_pt: 14,
        font_weight: 400,
        color_hex: "#000000",
        text_align: "left",
        line_height: 1.5,
        padding_mm: 2
      },
      box_mm: { w: 170, h: 60 }
    };
  }

  function getDefaultTextBoxMm(docData: any, pageIndex: number) {
    const margins = normalizePageMarginsMm(docData?.meta?.page_margins_mm);
    const kind = String(docData?.pages?.[pageIndex]?.kind || "single");
    const isSpread = kind === "spread";
    const boxes = getMarginBoxesMm({
      trimWmm: contentPageWidthMm,
      trimHmm: contentPageHeightMm,
      margins,
      isSpread
    });

    return boxes[0] || { x: 20, y: 20, w: 120, h: 60 };
  }

  function addTextWithRole(role: "heading" | "body" | "list") {
    if (readOnly) return;
    const { doc, docData, setDocData, pageIndex } = getActiveDocCtx(store);
    if (!docData) return;
    const preset = getTextPreset(role);
    const box = doc === "content"
      ? getDefaultTextBoxMm(docData, pageIndex)
      : { x: 20, y: 20, w: 120, h: 60 };

    const pages = Array.isArray(docData.pages) ? [...docData.pages] : [];
    const page = pages[pageIndex] || { elements: [] };
    const els = Array.isArray(page.elements) ? [...page.elements] : [];
    const id = `el_${Math.random().toString(16).slice(2)}`;
    const w_mm = Math.max(10, Math.min(Number(preset.box_mm?.w ?? box.w), Number(box.w ?? 120)));
    const h_mm = Math.max(10, Math.min(Number(preset.box_mm?.h ?? Math.min(30, box.h)), Number(box.h ?? 60)));
    const listMarker = role === "list" ? String(preset.list_marker || "bullet") : undefined;

    els.push({
      id,
      type: "text",
      text_role: role,
      ...(role === "list" ? { list_marker: listMarker } : {}),
      x_mm: Number(box.x ?? 20),
      y_mm: Number(box.y ?? 20),
      w_mm,
      h_mm,
      text: role === "heading" ? "Heading" : role === "list" ? "Item 1\nItem 2" : "Text",
      style: preset.style
    });

    pages[pageIndex] = { ...page, elements: els };
    setDocData({ ...docData, pages });
    store.selectElement(doc, id);
    store.bringToFront(doc, id);
    requestAnimationFrame(() => centerOnSelection());
  }

  function getSelectedText() {
    const selectedDoc = store.selected.doc;
    const selectedId = store.selected.elementId;
    if (!selectedId) return null;

    const docData = selectedDoc === "content" ? store.contentData : store.coverData;
    if (!docData) return null;

    const found = findElementById(docData, selectedId);
    if (!found?.el || found.el.type !== "text") return null;

    return {
      doc: selectedDoc,
      id: selectedId,
      el: found.el,
      pageIndex: found.pageIndex,
      docData
    };
  }

  function pasteElementFrom(el: any) {
    if (readOnly) return;
    const { doc, docData, setDocData, pageIndex } = getActiveDocCtx(store);
    if (!docData) return;

    const pages = Array.isArray(docData.pages) ? [...docData.pages] : [];
    const page = pages[pageIndex] || { elements: [] };
    const els = Array.isArray(page.elements) ? [...page.elements] : [];
    const id = `el_${Math.random().toString(16).slice(2)}`;
    const next = {
      ...cloneJson(el),
      id,
      x_mm: Number(el.x_mm ?? 0) + 5,
      y_mm: Number(el.y_mm ?? 0) + 5
    };

    els.push(next);
    pages[pageIndex] = { ...page, elements: els };
    setDocData({ ...docData, pages });

    store.selectElement(doc, id);
    store.bringToFront(doc, id);
    requestAnimationFrame(() => centerOnSelection());
  }

  async function onCopySelected() {
    const selected = getSelectedText();
    if (!selected) return;

    copiedTextRef.current = cloneJson(selected.el);

    try {
      await navigator.clipboard.writeText(JSON.stringify({ kind: "editor-text", el: selected.el }));
      showToast("Copied", "ok");
    } catch {
      showToast("Copied (local)", "ok");
    }
  }

  async function onPaste() {
    if (readOnly) return;
    try {
      const text = await navigator.clipboard.readText();
      const parsed = JSON.parse(text);
      if (parsed?.kind === "editor-text" && parsed?.el?.type === "text") {
        pasteElementFrom(parsed.el);
        return;
      }
    } catch {
      // Ignore clipboard read errors and fall back to local copy buffer.
    }

    if (copiedTextRef.current?.type === "text") {
      pasteElementFrom(copiedTextRef.current);
      return;
    }

    showToast("Nothing to paste", "error");
  }

  function onDuplicate() {
    if (readOnly) return;
    duplicateSelected(store);
  }

  async function saveTextPreset(
    role: "heading" | "body" | "list",
    preset: { style: any; box_mm: { w: number; h: number }; list_marker?: "bullet" | "number" }
  ) {
    if (readOnly) return;
    if (!store.projectId) return;

    const nextTextPresets = {
      ...(projectSettings?.text_presets || {}),
      [role]: preset
    };

    const res = await apiPut<{ settings: any }>(`projects/${store.projectId}/settings`, {
      settings: { text_presets: nextTextPresets }
    });

    setProjectSettings(res.settings || {});
    showToast(t("saved"), "ok");
  }

  /**
   * Add a rect element to the active doc/page.
   */
  function addRect() {
    if (readOnly) return;
    const { doc, docData, setDocData, pageIndex } = getActiveDocCtx(store);
    if (!docData) return;

    const pages = Array.isArray(docData.pages) ? [...docData.pages] : [];
    const page = pages[pageIndex] || { elements: [] };
    const els = Array.isArray(page.elements) ? [...page.elements] : [];
    const id = `el_${Math.random().toString(16).slice(2)}`;

    els.push({
      id,
      type: "rect",
      x_mm: 20,
      y_mm: 60 + els.length * 10,
      w_mm: 60,
      h_mm: 30,
      style: { fill_hex: "#ff0000" },
    });

    pages[pageIndex] = { ...page, elements: els };
    setDocData({ ...docData, pages });
    store.selectElement(doc, id);
  }

  // Insert a vector shape element with default fill/stroke style.
  function addShape(shape_id: ShapeId) {
    if (readOnly) return;
    const { doc, docData, setDocData, pageIndex } = getActiveDocCtx(store);
    if (!docData) return;

    const pages = Array.isArray(docData.pages) ? [...docData.pages] : [];
    const page = pages[pageIndex] || { elements: [] };
    const els = Array.isArray(page.elements) ? [...page.elements] : [];
    const id = `el_${Math.random().toString(16).slice(2)}`;

    els.push({
      id,
      type: "shape",
      shape_id,
      x_mm: 30,
      y_mm: 30,
      w_mm: 60,
      h_mm: 60,
      rotate_deg: 0,
      opacity: 1,
      style: {
        fill_hex: "#111827",
        stroke_hex: "#111827",
        stroke_width_mm: 0
      }
    });

    pages[pageIndex] = { ...page, elements: els };
    setDocData({ ...docData, pages });
    store.selectElement(doc, id);
    store.bringToFront(doc, id);
    requestAnimationFrame(() => centerOnSelection());
  }

  /**
   * Add a background element to the active doc/page.
   * Background covers the full artboard including bleed and stays on the back layer.
   * If a background already exists on the current page/unit, update it instead of duplicating.
   */
  function addBackground(fillOverride?: string) {
    if (readOnly) return;
    const { doc, docData, setDocData, pageIndex } = getActiveDocCtx(store);
    if (!docData) return;

    const pages = Array.isArray(docData.pages) ? [...docData.pages] : [];
    const page = pages[pageIndex] || { elements: [] };
    const els = Array.isArray(page.elements) ? [...page.elements] : [];
    const bounds = getArtboardBoundsMm(doc, pageIndex);
    const bgIdx = els.findIndex((item: any) => item?.type === "background" || item?.is_background);
    const fill = fillOverride || bgColor;
    const bgEl = {
      id: bgIdx >= 0 ? els[bgIdx].id : `bg_${Math.random().toString(16).slice(2)}`,
      type: "background",
      is_background: true,
      x_mm: bounds.x_mm,
      y_mm: bounds.y_mm,
      w_mm: bounds.w_mm,
      h_mm: bounds.h_mm,
      rotate_deg: 0,
      style: { fill_hex: fill, background_kind: "solid" },
    };

    let nextEls = els;
    if (bgIdx >= 0) {
      nextEls = [...els];
      nextEls[bgIdx] = { ...els[bgIdx], ...bgEl };
    } else {
      nextEls = [bgEl, ...els];
    }

    pages[pageIndex] = { ...page, elements: nextEls };
    setDocData({ ...docData, pages });
    store.selectElement(doc, bgEl.id);
  }

  function applySolidBackgroundColor(fill: string) {
    const nextFill = /^#[0-9a-fA-F]{6}$/.test(fill) ? fill : bgColor;
    setBgColor(nextFill);
    addBackground(nextFill);
  }

  // English comment: Upsert page background and apply a repeatable SVG pattern.
  function applyBackgroundPattern(patternId: string) {
    if (readOnly) return;
    const pattern = getBackgroundPatternById(patternId);
    if (!pattern) return;
    const { doc, docData, setDocData, pageIndex } = getActiveDocCtx(store);
    if (!docData) return;

    const pages = Array.isArray(docData.pages) ? [...docData.pages] : [];
    const page = pages[pageIndex] || { elements: [] };
    const els = Array.isArray(page.elements) ? [...page.elements] : [];
    const bounds = getArtboardBoundsMm(doc, pageIndex);
    const bgIdx = els.findIndex((item: any) => item?.type === "background" || item?.is_background);
    const existing = bgIdx >= 0 ? els[bgIdx] : null;
    const bgEl = {
      ...(existing || {}),
      id: existing?.id || `bg_${Math.random().toString(16).slice(2)}`,
      type: "background",
      is_background: true,
      x_mm: bounds.x_mm,
      y_mm: bounds.y_mm,
      w_mm: bounds.w_mm,
      h_mm: bounds.h_mm,
      rotate_deg: 0,
      style: {
        ...(existing?.style || {}),
        fill_hex: existing?.style?.fill_hex || bgColor || "#FFFFFF",
        background_kind: "pattern",
        pattern_id: pattern.id,
        pattern_repeat: "repeat",
        pattern_scale: Number(existing?.style?.pattern_scale || 1),
        pattern_opacity: Number(existing?.style?.pattern_opacity ?? 1),
      },
    };

    const nextEls = bgIdx >= 0 ? [...els] : [bgEl, ...els];
    if (bgIdx >= 0) nextEls[bgIdx] = bgEl;
    pages[pageIndex] = { ...page, elements: nextEls };
    setDocData({ ...docData, pages });
    store.selectElement(doc, bgEl.id);
  }

  useEffect(() => {
    /**
     * Rehydrate persisted image assets if a snapshot enters the editor without preview URLs.
     */
    if (!store.projectId) return;
    if (!docHasImageAssetsMissingUrl(store.contentData) && !docHasImageAssetsMissingUrl(store.coverData)) return;
    if (rehydrateImageUrlsInFlightRef.current) return;

    rehydrateImageUrlsInFlightRef.current = true;
    (async () => {
      try {
        if (store.contentData) {
          const hydrated = await hydrateDocImageUrls(store.contentData);
          if (hydrated !== store.contentData) store.setContentData(hydrated);
        }
        if (store.coverData) {
          const hydrated = await hydrateDocImageUrls(store.coverData);
          if (hydrated !== store.coverData) store.setCoverData(hydrated);
        }
      } catch {
        // Ignore preview URL refresh failures; persisted asset_id still drives exports.
      } finally {
        rehydrateImageUrlsInFlightRef.current = false;
      }
    })();
  }, [store, store.coverData, store.contentData, store.projectId]);

  useEffect(() => {
    if (!store.projectId) return;
    if (imagePreloadProjectIdRef.current === store.projectId) return;
    imagePreloadProjectIdRef.current = store.projectId;
    initialImagePreloadDoneRef.current = false;
    preloadedImageUrlsRef.current = new Set();
    setProjectImagePreload({ running: false, total: 0, done: 0 });
  }, [store.projectId]);

  useEffect(() => {
    if (!store.projectId) return;

    const assetIds = new Set<number>([
      ...collectAssetIds(store.contentData),
      ...collectAssetIds(store.coverData)
    ]);
    const previewUrls = collectImagePreviewUrls(store.contentData, store.coverData);
    const hasImageContent = assetIds.size > 0 || previewUrls.length > 0;

    if (!hasImageContent) {
      initialImagePreloadDoneRef.current = true;
      setProjectImagePreload((current) => (current.running ? { running: false, total: 0, done: 0 } : current));
      return;
    }

    const isInitialPreload = !initialImagePreloadDoneRef.current;

    if (previewUrls.length === 0) {
      if (!isInitialPreload) return;

      const total = Math.max(1, assetIds.size);
      setProjectImagePreload({ running: true, total, done: 0 });
      const fallbackTimer = window.setTimeout(() => {
        if (initialImagePreloadDoneRef.current) return;
        initialImagePreloadDoneRef.current = true;
        setProjectImagePreload({ running: false, total, done: total });
      }, 12000);

      return () => window.clearTimeout(fallbackTimer);
    }

    const urls = isInitialPreload
      ? previewUrls
      : previewUrls.filter((url) => !preloadedImageUrlsRef.current.has(url));
    if (urls.length === 0) {
      if (isInitialPreload) {
        initialImagePreloadDoneRef.current = true;
        setProjectImagePreload({ running: false, total: previewUrls.length, done: previewUrls.length });
      }
      return;
    }

    urls.forEach((url) => preloadedImageUrlsRef.current.add(url));
    const runId = ++projectImagePreloadRunRef.current;
    let cancelled = false;
    if (isInitialPreload) {
      setProjectImagePreload({ running: true, total: urls.length, done: 0 });
    }

    const timer = window.setTimeout(() => {
      preloadCanvasImageUrls(urls, (done, total) => {
        if (cancelled || !isInitialPreload || projectImagePreloadRunRef.current !== runId) return;
        setProjectImagePreload({ running: true, total, done });
      })
        .catch(() => {
          // Preview images are opportunistic; missing cache should not block editing.
        })
        .finally(() => {
          if (cancelled || !isInitialPreload || projectImagePreloadRunRef.current !== runId) return;
          initialImagePreloadDoneRef.current = true;
          setProjectImagePreload({ running: false, total: urls.length, done: urls.length });
        });
    }, 80);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [store.contentData, store.coverData, store.projectId]);

  useEffect(() => {
    if (coverMode !== "separate" && store.coverPageIndex !== 0) {
      store.setCoverPageIndex(0);
    }
  }, [coverMode, store]);

  useEffect(() => {
    /**
     * Periodically renew presigned image URLs so long editing sessions don't break (403).
     */
    if (!store.projectId) return;

    const t = setInterval(async () => {
      try {
        const current = useEditorStore.getState();
        if (current.contentData) {
          const hydrated = await hydrateDocImageUrls(current.contentData);
          current.setContentData(hydrated);
        }
        if (current.coverData) {
          const hydrated = await hydrateDocImageUrls(current.coverData);
          current.setCoverData(hydrated);
        }
      } catch {
        // ignore; next cycle will retry
      }
    }, 5 * 60 * 1000);

    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store.projectId]);

  useEffect(() => {
    /**
     * Keyboard shortcuts:
     * - Delete / Backspace: delete selected element
     * - Ctrl/Cmd + D: duplicate selected element
     * - Arrow keys: move selected element by 1mm (Shift: 10mm)
     *
     * Guard: do not trigger while typing in inputs/textareas/contenteditable.
     */
    function isTypingTarget(e: KeyboardEvent) {
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName?.toLowerCase();

      return (
        tag === "input" ||
        tag === "textarea" ||
        (target?.getAttribute?.("contenteditable") === "true")
      );
    }

    function onKeyDown(e: KeyboardEvent) {
      if (e.defaultPrevented) return;
      if (isTypingTarget(e)) return;
      if (readOnly) return;

      // Undo: Ctrl/Cmd + Z
      if ((e.ctrlKey || e.metaKey) && (e.key === "z" || e.key === "Z") && !e.shiftKey) {
        e.preventDefault();
        if (store.canUndo()) store.undo();
        return;
      }

      // Redo: Ctrl/Cmd + Shift + Z
      if ((e.ctrlKey || e.metaKey) && (e.key === "z" || e.key === "Z") && e.shiftKey) {
        e.preventDefault();
        if (store.canRedo()) store.redo();
        return;
      }

      // Redo (Windows): Ctrl + Y
      if (e.ctrlKey && (e.key === "y" || e.key === "Y")) {
        e.preventDefault();
        if (store.canRedo()) store.redo();
        return;
      }

      const doc = store.selected.doc;
      const id = store.selected.elementId;

      // Duplicate: Ctrl/Cmd + D
      if ((e.ctrlKey || e.metaKey) && (e.key === "d" || e.key === "D")) {
        e.preventDefault();
        duplicateSelected(store);
        return;
      }

      // Delete selected element
      if (e.key === "Delete" || e.key === "Backspace") {
        if (!id) return;
        e.preventDefault();
        store.deleteElement(doc, id);
        return;
      }

      // Move selected element with arrows
      const isArrow =
        e.key === "ArrowLeft" ||
        e.key === "ArrowRight" ||
        e.key === "ArrowUp" ||
        e.key === "ArrowDown";

      if (!isArrow || !id) return;

      e.preventDefault();

      const stepMm = e.shiftKey ? 10 : 1;

      // Compute delta in mm
      let dx = 0;
      let dy = 0;
      if (e.key === "ArrowLeft") dx = -stepMm;
      if (e.key === "ArrowRight") dx = stepMm;
      if (e.key === "ArrowUp") dy = -stepMm;
      if (e.key === "ArrowDown") dy = stepMm;

      // Read current position from doc data
      const docData = doc === "content" ? store.contentData : store.coverData;
      if (!docData) return;

      const found = findElementById(docData, id);
      if (!found) return;

      const current = found.el;
      const x = Number(current.x_mm ?? 0) + dx;
      const y = Number(current.y_mm ?? 0) + dy;

      store.updateElement(doc, id, { x_mm: x, y_mm: y });
    }

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [store, readOnly]);

  function openImagesForPlaceholder(payload: { doc: "content" | "cover"; pageIndex: number; elementId: string }) {
    if (readOnly) return;
    store.setActiveDoc(payload.doc);
    if (payload.doc === "content") {
      store.setContentUnitIndex(payload.pageIndex);
    } else {
      store.setCoverPageIndex(payload.pageIndex);
    }
    store.selectElement(payload.doc, payload.elementId);
    setPropsDrawerOpen(false);
    setShowImages(true);
  }

  function replaceSelectedImagePlaceholderWithAsset(args: {
    assetId: number;
    imageUrl?: string;
  }) {
    const selectedDoc = store.selected.doc;
    const selectedId = store.selected.elementId;
    if (!selectedId) return false;

    const docData = selectedDoc === "content" ? store.contentData : store.coverData;
    const setDocData = selectedDoc === "content" ? store.setContentData : store.setCoverData;
    if (!docData) return false;

    const found = findElementById(docData, selectedId);
    if (!found?.el || !isImagePlaceholderElement(found.el)) return false;

    const pages = Array.isArray(docData.pages) ? [...docData.pages] : [];
    const page = pages[found.pageIndex] || { elements: [] };
    const els = Array.isArray(page.elements) ? [...page.elements] : [];
    const base = { ...found.el };
    delete base.placeholder;
    delete base.placeholder_kind;
    delete base.clipart_id;
    delete base.image_src;
    delete base.local_asset_path;
    delete base._ghost;
    delete base._ghost_key;

    els[found.elementIndex] = {
      ...base,
      type: "image",
      source: "asset",
      asset_id: args.assetId,
      image_url: args.imageUrl,
      image_mode: String(base.image_mode || "frame"),
      fit: base.fit === "contain" ? "contain" : "cover",
      scale: 1,
      pan_mm: { x: 0, y: 0 },
      opacity: base.opacity == null ? 1 : base.opacity,
      rotate_deg: base.rotate_deg == null ? 0 : base.rotate_deg
    };

    pages[found.pageIndex] = { ...page, elements: els };
    setDocData({ ...docData, pages });
    store.selectElement(selectedDoc, selectedId);
    return true;
  }

  async function insertAsset(asset: AssetLibraryItem) {
    if (readOnly) return;
    const doc = store.activeDoc;
    const docData = doc === "content" ? store.contentData : store.coverData;
    if (!docData) return;

    try {
      const dl = await apiGet<{ download_url: string }>(`assets/${asset.id}/download-url`);
      const url = dl.download_url;

      if (replaceSelectedImagePlaceholderWithAsset({ assetId: asset.id, imageUrl: url })) {
        return;
      }

      const pageIndex = doc === "content" ? store.contentUnitIndex : store.coverPageIndex;
      const pages = Array.isArray(docData.pages) ? [...docData.pages] : [{ elements: [] }];
      const page = pages[pageIndex] || { elements: [] };
      const els = Array.isArray(page.elements) ? [...page.elements] : [];
      const { w_mm, h_mm } = defaultImageFrameMm(asset.width_px, asset.height_px);
      const centered = getCenteredImageInsertPosition(doc, pageIndex, w_mm, h_mm);

      els.push({
        id: `el_${Math.random().toString(16).slice(2)}`,
        type: "image",
        source: "asset",
        asset_id: asset.id,
        image_url: url,
        image_mode: "free",
        x_mm: centered.x_mm,
        y_mm: centered.y_mm,
        w_mm,
        h_mm,
        fit: "contain",
        scale: 1,
        opacity: 1,
        rotate_deg: 0,
        pan_mm: { x: 0, y: 0 }
      });

      pages[pageIndex] = { ...page, elements: els };
      if (doc === "content") store.setContentData({ ...docData, pages });
      else store.setCoverData({ ...docData, pages });
    } catch (e: any) {
      alert(String(e?.message || e));
    }
  }

  function insertClipart(item: ClipartItem) {
    if (readOnly) return;
    const { doc, docData, setDocData, pageIndex } = getActiveDocCtx(store);
    if (!docData) return;

    const pages = Array.isArray(docData.pages) ? [...docData.pages] : [];
    const page = pages[pageIndex] || { elements: [] };
    const els = Array.isArray(page.elements) ? [...page.elements] : [];
    const id = `el_${Math.random().toString(16).slice(2)}`;
    const clipartId = String(item.path || item.id || "");
    const { w_mm, h_mm } = defaultImageFrameMm(item.width, item.height);
    const centered = getCenteredImageInsertPosition(doc, pageIndex, w_mm, h_mm);

    els.push({
      id,
      type: "image",
      source: "clipart",
      clipart_id: clipartId,
      asset_id: null,
      image_url: item.url || getClipartPublicUrl(clipartId),
      image_mode: "free",
      x_mm: centered.x_mm,
      y_mm: centered.y_mm,
      w_mm,
      h_mm,
      fit: "contain",
      scale: 1,
      opacity: 1,
      rotate_deg: 0,
      pan_mm: { x: 0, y: 0 }
    });

    pages[pageIndex] = { ...page, elements: els };
    setDocData({ ...docData, pages });
    store.selectElement(doc, id);
  }

  /**
   * Insert dropped asset into a specific doc/unit at x/y in mm.
   */
  async function insertAssetAt({
    targetDoc,
    targetUnitIndex,
    asset_id,
    image_url,
    width_px,
    height_px,
    x_mm,
    y_mm
  }: {
    targetDoc: "content" | "cover";
    targetUnitIndex: number;
    asset_id: number;
    image_url?: string;
    width_px?: number;
    height_px?: number;
    x_mm: number;
    y_mm: number;
  }) {
    if (readOnly) return;
    const docData = targetDoc === "content" ? store.contentData : store.coverData;
    const setDocData = targetDoc === "content" ? store.setContentData : store.setCoverData;
    if (!docData) return;
    let previewUrl = image_url;
    if (!previewUrl) {
      try {
        const dl = await apiGet<{ download_url: string }>(`assets/${asset_id}/download-url`);
        previewUrl = dl.download_url;
      } catch {
        previewUrl = undefined;
      }
    }

    const pages = Array.isArray(docData.pages) ? [...docData.pages] : [];
    const unit = pages[targetUnitIndex] || { elements: [] };
    const els = Array.isArray(unit.elements) ? [...unit.elements] : [];

    const id = `el_${Math.random().toString(16).slice(2)}`;
    const { w_mm, h_mm } = defaultImageFrameMm(width_px, height_px);

    els.push({
      id,
      type: "image",
      source: "asset",
      image_mode: "free",
      asset_id,
      image_url: previewUrl,
      x_mm,
      y_mm,
      w_mm,
      h_mm,
      rotate_deg: 0,
      opacity: 1
    });

    pages[targetUnitIndex] = { ...unit, elements: els };
    setDocData({ ...docData, pages });
    store.selectElement(targetDoc, id);
  }

  async function onPickImage(e: React.ChangeEvent<HTMLInputElement>) {
    if (readOnly) {
      e.target.value = "";
      return;
    }
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setUploading(true);

      const uploaded = await uploadImageToPrintApi(file, store.projectId);
      requestImageLibraryRefresh();

      if (replaceSelectedImagePlaceholderWithAsset({ assetId: uploaded.asset_id, imageUrl: uploaded.download_url })) {
        setShowImages(false);
        return;
      }

      const { doc, docData, setDocData, pageIndex } = getActiveDocCtx(store);
      if (!docData) return;

      const pages = Array.isArray(docData.pages) ? [...docData.pages] : [];
      const page = pages[pageIndex] || { elements: [] };
      const els = Array.isArray(page.elements) ? [...page.elements] : [];
      const id = `el_${Math.random().toString(16).slice(2)}`;
      const centered = getCenteredImageInsertPosition(doc, pageIndex, 80, 60);

      els.push({
        id,
        type: "image",
        source: "asset",
        asset_id: uploaded.asset_id,
        image_url: uploaded.download_url, // preview only
        image_mode: "free",
        x_mm: centered.x_mm,
        y_mm: centered.y_mm,
        w_mm: 80,
        h_mm: 60,
        fit: "cover",
        scale: 1,
        opacity: 1,
        rotate_deg: 0,
        pan_mm: { x: 0, y: 0 }
      });

      pages[pageIndex] = { ...page, elements: els };
      setDocData({ ...docData, pages });
      store.selectElement(doc, id);
    } catch (err: any) {
      alert(String(err?.message || err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  }

  async function onBulkUpload(e: React.ChangeEvent<HTMLInputElement>) {
    if (readOnly) {
      e.target.value = "";
      return;
    }
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    const projectId = store.projectId;
    if (!projectId) return alert("Project not ready yet.");

    setBulkProgress({ total: files.length, done: 0, running: true });

    try {
      // Limit concurrency to avoid saturating browser + MinIO
      await runWithLimit(files, 4, async (file) => {
        await uploadImageToPrintApi(file, projectId);
        setBulkProgress((p) => ({ ...p, done: p.done + 1 }));
      });

      // Ask ImageLibraryPanel to reload once after the batch completes.
      requestImageLibraryRefresh();
    } catch (err: any) {
      alert(String(err?.message || err));
    } finally {
      setBulkProgress((p) => ({ ...p, running: false }));
    }
  }

  /**
   * Save current drafts immediately (content + cover).
   */
  async function saveDraftsNow(options?: {
    contentData?: any;
    coverData?: any;
    product?: ProductConfig | null;
  }) {
    if (!store.projectId) return;
    const contentDataForSave = options?.contentData || store.contentData;
    const coverDataForInput = options?.coverData || store.coverData;
    if (!contentDataForSave || !coverDataForInput) return;
    if (readOnly) return;

    setSaveReturnError(null);

    const projectId = store.projectId;
    const coverDataForSave = getSyncedCoverDataForPersist(
      coverDataForInput,
      contentDataForSave,
      options?.product ?? product
    );
    if (!options?.coverData && coverDataForSave !== store.coverData) store.setCoverData(coverDataForSave);

    await apiPut(`projects/${projectId}/draft`, { data: stripPreviewFields(contentDataForSave) });
    await apiPut(`projects/${projectId}/cover-draft`, { data: stripPreviewFields(coverDataForSave) });

    setLastSaveAt(new Date().toLocaleTimeString());
    return { contentData: contentDataForSave, coverData: coverDataForSave };
  }

  /**
   * Show a temporary toast message.
   */
  function showToast(message: string, kind: "ok" | "error" = "ok") {
    setToast({ message, kind });
    window.setTimeout(() => setToast(null), 2200);
  }

  async function loadCompatibleVariants(force = false) {
    if (!product) return;
    if (!force && compatibleVariantsForSku === product.sku && compatibleVariants.length > 0) return;

    try {
      setVariantLoading(true);
      setVariantError(null);
      const res = await apiGet<CompatibleVariantsResponse>(
        `products/by-sku/${encodeURIComponent(product.sku)}/compatible-variants`
      );
      const variants = Array.isArray(res.variants) ? res.variants : [];
      setCompatibleVariants(variants);
      setCompatibleVariantsForSku(product.sku);
      setSelectedVariantSku((prev) => {
        if (prev && variants.some((item) => item.sku === prev)) return prev;
        return product.sku;
      });
    } catch (e: any) {
      const msg = String(e?.message || e);
      setVariantError(msg);
      showToast(msg, "error");
    } finally {
      setVariantLoading(false);
    }
  }

  function openOrderReview() {
    setSaveReturnError(null);
    setVariantError(null);
    setOrderDialogMode("review");
  }

  async function openVariantPicker() {
    setCoverChangeAccepted(false);
    setOrderDialogMode("variant");
    await loadCompatibleVariants();
  }

  function selectVariantByOptions(patch: { binding?: BindingKey; coverFinish?: FinishKey; paperFinish?: FinishKey }) {
    const current = compatibleVariants.find((item) => item.sku === selectedVariantSku) || product;
    const nextBinding = patch.binding || bindingKeyFromProduct(current);
    const nextCover = patch.coverFinish || coverFinishFromProduct(current);
    const nextPaper = patch.paperFinish || paperFinishFromProduct(current);
    const sameBinding = compatibleVariants.filter((item) => bindingKeyFromProduct(item) === nextBinding);
    const hasKnownCoverFinish = sameBinding.some((item) => coverFinishFromProduct(item) !== "unknown");
    const coverCandidates = sameBinding.filter((item) => {
      const finish = coverFinishFromProduct(item);
      if (nextCover === "unknown" && hasKnownCoverFinish) return finish !== "unknown";
      return finish === nextCover;
    });
    const exact = coverCandidates.find(
      (item) =>
        paperFinishFromProduct(item) === nextPaper
    );
    const fallback =
      coverCandidates[0] ||
      sameBinding.find((item) => paperFinishFromProduct(item) === nextPaper && coverFinishFromProduct(item) !== "unknown") ||
      sameBinding.find((item) => coverFinishFromProduct(item) !== "unknown") ||
      sameBinding[0] ||
      compatibleVariants.find((item) => patch.coverFinish && coverFinishFromProduct(item) === patch.coverFinish) ||
      compatibleVariants.find((item) => patch.paperFinish && paperFinishFromProduct(item) === patch.paperFinish);
    const next = exact || fallback;
    if (next) {
      setSelectedVariantSku(next.sku);
      setCoverChangeAccepted(false);
    }
  }

  async function applySelectedVariant() {
    if (!store.projectId || !project || !product) return;
    const targetSku = selectedVariantSku || product.sku;
    if (!targetSku || targetSku === product.sku) {
      setOrderDialogMode("review");
      return;
    }

    const targetProduct = compatibleVariants.find((item) => item.sku === targetSku);
    if (!targetProduct) {
      const msg = locale === "pl" ? "Nie znaleziono wybranego wariantu." : "Selected variant was not found.";
      setVariantError(msg);
      showToast(msg, "error");
      return;
    }
    const coverGeometryChanged = coverGeometryChangedBetweenProducts(product, targetProduct);
    if (coverGeometryChanged && !coverChangeAccepted) {
      const msg = locale === "pl"
        ? "Zaakceptuj zmianę geometrii okładki przed zastosowaniem wariantu."
        : "Accept the cover geometry change before applying this variant.";
      setVariantError(msg);
      showToast(msg, "error");
      return;
    }

    try {
      setChangingVariant(true);
      setVariantError(null);

      const changed = await apiPut<ProjectVariantChangeResponse>(`projects/${store.projectId}/variant`, {
        sku: targetSku,
        cover_geometry_accepted: coverGeometryChanged && coverChangeAccepted,
      });
      const nextProduct = changed.product || targetProduct;
      const changedCoverGeometry =
        Boolean(changed.cover_geometry_changed || changed.requires_cover_review) ||
        coverGeometryChangedBetweenProducts(product, nextProduct);
      const docs = buildDocsForProductVariant(nextProduct);
      if (!docs) {
        throw new Error(locale === "pl" ? "Projekt nie jest gotowy do zmiany wariantu." : "Project is not ready for variant change.");
      }

      setProduct(nextProduct);
      store.setProject({
        id: Number(changed.project_id || store.projectId),
        public_id: changed.public_id || project.public_id,
        sku: nextProduct.sku,
      });
      store.setContentData(docs.contentData);
      store.setCoverData(docs.coverData);

      await saveDraftsNow({
        contentData: docs.contentData,
        coverData: docs.coverData,
        product: nextProduct,
      });

      replaceEditorUrlSku(nextProduct.sku);
      setCompatibleVariants([]);
      setCompatibleVariantsForSku(null);
      setSelectedVariantSku(nextProduct.sku);
      setCoverChangeAccepted(false);
      if (changedCoverGeometry) {
        store.setActiveDoc("cover");
        store.setCoverPageIndex(0);
      }
      setOrderDialogMode("review");
      showToast(
        changedCoverGeometry
          ? locale === "pl"
            ? "Wariant zmieniony. Sprawdź okładkę."
            : "Variant changed. Review the cover."
          : locale === "pl"
            ? "Wariant produktu zmieniony."
            : "Product variant changed.",
        "ok"
      );
    } catch (e: any) {
      const msg = String(e?.message || e);
      setVariantError(msg);
      showToast(msg, "error");
    } finally {
      setChangingVariant(false);
    }
  }

  function openMobileTool(action: HelpAction) {
    setPropsDrawerOpen(false);
    setMobileToolAction(action);
    setMobileToolActionNonce((n) => n + 1);
  }

  function openPropertiesForSelectedElement() {
    if (readOnly) return;
    setToolsCollapsed(true);
    setShowImages(false);
    setPropsDrawerOpen(true);
  }

  function markOnboardingGuideSeen() {
    try {
      window.localStorage.setItem(EDITOR_ONBOARDING_STORAGE_KEY, "done");
    } catch {
      // ignore storage errors
    }
  }

  function closeOnboardingGuide() {
    markOnboardingGuideSeen();
    setShowOnboardingGuide(false);
  }

  function openOnboardingGuide() {
    setShowShare(false);
    setShowHelp(false);
    setShowOnboardingGuide(true);
  }

  function handleOnboardingAction(action: HelpAction) {
    closeOnboardingGuide();
    handleHelpAction(action);
  }

  // English comment: Dispatch help deeplink actions to the appropriate UI part.
  function handleHelpAction(action: HelpAction) {
    if (action.type === "open_share") {
      setShowShare(true);
      return;
    }

    setHelpAction(action);
    setHelpActionNonce((n) => n + 1);
    openMobileTool(action);
  }

  /**
   * Save immutable snapshots (content + cover), then open the private purchase review on the Woo product page.
   */
  async function saveAndReturnToShop() {
    if (!store.projectId) return;
    if (!store.contentData || !store.coverData) return;
    if (readOnly) {
      const msg = lock?.locked && !lock?.mine
        ? locale === "pl"
          ? `Projekt edytuje teraz ${lock.by || "inny użytkownik"}.`
          : `Project is currently locked by ${lock.by || "another user"}.`
        : locale === "pl"
          ? "Nie masz aktywnej blokady edycji."
          : "You do not have the edit lock.";
      setSaveReturnError(msg);
      showToast(msg, "error");
      return;
    }
    if (!canOrder) {
      const msg = locale === "pl" ? "Gość nie może złożyć zamówienia." : "Guests cannot place orders.";
      setSaveReturnError(msg);
      showToast(msg, "error");
      return;
    }

    const pagesCount = physicalPageCountFromContentDoc(store.contentData);
    const minPages = product?.min_pages ?? 24;
    const maxPages = product?.max_pages ?? 190;

    if (pagesCount < minPages) {
      setSaveReturnError(`Content has ${pagesCount} pages. Minimum for this product is ${minPages}.`);
      return;
    }
    if (pagesCount > maxPages) {
      setSaveReturnError(`Content has ${pagesCount} pages. Maximum for this product is ${maxPages}.`);
      return;
    }

    const wpBase = process.env.NEXT_PUBLIC_WP_BASE_URL || process.env.NEXT_PUBLIC_WOO_BASE_URL;
    if (!wpBase) {
      setSaveReturnError("Missing NEXT_PUBLIC_WP_BASE_URL or NEXT_PUBLIC_WOO_BASE_URL in .env.local");
      return;
    }

    try {
      setSaving(true);
      setSaveReturnError(null);

      // Ensure latest drafts are saved before creating immutable snapshots.
      const savedDrafts = await saveDraftsNow();
      const contentDataForSnapshot = savedDrafts?.contentData || store.contentData;
      const coverDataForSnapshot = savedDrafts?.coverData || getSyncedCoverDataForPersist(store.coverData, contentDataForSnapshot);

      const contentSnap = await apiPost<any>(`projects/${store.projectId}/snapshots`, {
        data: stripPreviewFields(contentDataForSnapshot),
      });

      const coverSnap = await apiPost<any>(`projects/${store.projectId}/cover-snapshots`, {
        data: stripPreviewFields(coverDataForSnapshot),
      });

      const contentSnapshotId = contentSnap.id;
      const coverSnapshotId = coverSnap.id;
      const projectPublicIdForCart = store.projectPublicId || project?.public_id || projectPublicId || null;
      const claimTokenForCart = getProjectClaimToken(projectPublicIdForCart);

      if (!projectPublicIdForCart) {
        throw new Error("Project public id missing for cart token");
      }

      // English comment: Use top-level POST navigation to Woo so WP login cookies are available reliably.
      const action = `${wpBase.replace(/\/+$/, "")}/wp-json/print-app/v1/cart-token-redirect`;
      const form = document.createElement("form");
      form.method = "POST";
      form.action = action;
      form.style.display = "none";

      const fields: Record<string, string> = {
        sku: String(product?.sku || sku),
        project_public_id: String(projectPublicIdForCart),
        project_id: String(store.projectId || ""),
        content_snapshot_id: String(contentSnapshotId),
        cover_snapshot_id: String(coverSnapshotId),
        quantity: "1",
      };
      if (claimTokenForCart) {
        fields.claim_token = claimTokenForCart;
      }

      for (const [name, value] of Object.entries(fields)) {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = name;
        input.value = value;
        form.appendChild(input);
      }

      document.body.appendChild(form);
      form.submit();
    } catch (e: any) {
      console.error("saveAndReturnToShop failed", e);
      setSaveReturnError(String(e?.message || e));
      showToast(String(e?.message || e), "error");
    } finally {
      setSaving(false);
    }
  }

  async function saveProjectAs(name: string) {
    if (copyRequestRef.current || readOnly || !canShare || !store.projectId || !store.contentData || !store.coverData) return;
    copyRequestRef.current = true;
    saveAsPausedRef.current = true;
    setCopyingProject(true);
    setCopyError(null);
    try {
      // Send current documents directly to the new project. Do not save them
      // over the source, and keep its session unchanged until a full navigation.
      const result = await apiPost<{ project: Project & { sku: string }; token: string; claim_token: string }>(
        `projects/${store.projectId}/copy`, {
          name,
          content_data: stripPreviewFields(store.contentData),
          cover_data: stripPreviewFields(getSyncedCoverDataForPersist(store.coverData, store.contentData)),
        }
      );
      try { persistProjectClaimToken(result.project.public_id, result.claim_token); } catch { /* Storage may be disabled. */ }
      const params = new URLSearchParams({ project: result.project.public_id, session: result.token });
      // Reset editor history, cached assets and outstanding effects between projects.
      window.location.assign(`/${locale}/editor/${encodeURIComponent(result.project.sku || sku)}?${params}`);
    } catch (e: any) {
      const status = String(e?.message || e);
      setCopyError(locale === "pl"
        ? /failed: (401|403|423)/.test(status)
          ? "Brak dostępu do kopiowania. Sprawdź sesję i blokadę edycji projektu."
          : "Nie udało się utworzyć kopii. Sprawdź połączenie i dostępność zdjęć, a następnie spróbuj ponownie."
        : /failed: (401|403|423)/.test(status)
          ? "Copying is unavailable. Check your session and the project edit lock."
          : "Could not create the copy. Check your connection and photo availability, then try again.");
      copyRequestRef.current = false;
      setCopyingProject(false);
    }
  }

  function renderOrderDialog() {
    if (!orderDialogMode || !product) return null;

    const isPl = locale === "pl";
    const pagesCount = physicalPageCountFromContentDoc(store.contentData);
    const selectedVariant = compatibleVariants.find((item) => item.sku === selectedVariantSku) || product;
    const variantItems = [...compatibleVariants].sort((a, b) =>
      productVariantLabel(a, locale).localeCompare(productVariantLabel(b, locale), locale)
    );
    const coverChoiceProducts = filterCoverChoiceProducts(variantItems);
    const coverChoices = Array.from(
      coverChoiceProducts.reduce((map, item) => {
        const key = coverChoiceKey(item);
        if (!map.has(key)) {
          map.set(key, {
            key,
            binding: bindingKeyFromProduct(item),
            finish: coverFinishFromProduct(item),
            label: productCoverLabel(item, locale),
          });
        }
        return map;
      }, new Map<string, { key: string; binding: BindingKey; finish: FinishKey; label: string }>())
      .values()
    ).sort((a, b) => a.label.localeCompare(b.label, locale));
    const paperOptions = Array.from(new Set(variantItems.map(paperFinishFromProduct).filter((item) => item !== "unknown")));
    const selectedBinding = bindingKeyFromProduct(selectedVariant);
    const selectedCoverFinish = coverFinishFromProduct(selectedVariant);
    const selectedPaperFinish = paperFinishFromProduct(selectedVariant);
    const selectedSpineMm = selectedVariant
      ? resolveProductSpineMm(selectedVariant, pagesCount)
      : coverSpineMm;
    const spineChanged = Math.abs(Number(selectedSpineMm || 0) - Number(coverSpineMm || 0)) >= 0.01;
    const selectedCoverGeometryChanged =
      selectedVariant.sku !== product.sku &&
      (Boolean((selectedVariant as VariantProduct).requires_cover_review || (selectedVariant as VariantProduct).cover_geometry_changed) ||
        coverGeometryChangedBetweenProducts(product, selectedVariant));

    return (
      <div className="fixed inset-0 z-[11030]">
        <button
          type="button"
          className="absolute inset-0 bg-black/45"
          aria-label={isPl ? "Zamknij" : "Close"}
          onClick={() => {
            if (!saving && !changingVariant) setOrderDialogMode(null);
          }}
        />
        <div className="fixed left-1/2 top-1/2 flex max-h-[88dvh] w-[560px] max-w-[94vw] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-xl border border-white/10 bg-[#0f141d] text-white shadow-2xl">
          <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
            <div>
              <div className="text-base font-semibold">
                {orderDialogMode === "review"
                  ? isPl ? "Zamówienie" : "Order"
                  : isPl ? "Zmień wariant produktu" : "Change product variant"}
              </div>
              <div className="mt-1 max-w-[420px] truncate text-xs text-white/60">{productTitle}</div>
            </div>
            <button
              type="button"
              className="rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-sm text-white hover:bg-white/10 disabled:opacity-50"
              onClick={() => setOrderDialogMode(null)}
              disabled={saving || changingVariant}
            >
              {isPl ? "Zamknij" : "Close"}
            </button>
          </div>

          {orderDialogMode === "review" ? (
            <div className="overflow-auto px-5 py-4">
              <div className="space-y-3">
                <div className="rounded-lg border border-white/10 bg-white/5 p-4">
                  <div className="mb-3 text-xs font-semibold uppercase tracking-[0.08em] text-white/45">
                    {isPl ? "Twój projekt" : "Your project"}
                  </div>
                  <dl className="grid gap-3 text-sm sm:grid-cols-2">
                    <div>
                      <dt className="text-white/45">{isPl ? "Format" : "Format"}</dt>
                      <dd className="mt-1 font-medium">{productFormatLabel(product.format_key, locale)}</dd>
                    </div>
                    <div>
                      <dt className="text-white/45">{isPl ? "Okładka" : "Cover"}</dt>
                      <dd className="mt-1 font-medium">{productCoverLabel(product, locale)}</dd>
                    </div>
                    <div>
                      <dt className="text-white/45">{isPl ? "Papier" : "Paper"}</dt>
                      <dd className="mt-1 font-medium">{paperLabel(paperFinishFromProduct(product), locale)}</dd>
                    </div>
                    <div>
                      <dt className="text-white/45">{isPl ? "Strony i grzbiet" : "Pages and spine"}</dt>
                      <dd className="mt-1 font-medium">
                        {pagesCount} · {coverSpineMm.toFixed(2)} mm
                      </dd>
                    </div>
                  </dl>
                </div>

                {saveReturnError ? (
                  <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-100">
                    {saveReturnError}
                  </div>
                ) : null}
              </div>

              <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  className="rounded-lg border border-white/10 bg-white/5 px-4 py-2 text-sm font-medium text-white hover:bg-white/10 disabled:opacity-50"
                  onClick={openVariantPicker}
                  disabled={saving || readOnly}
                >
                  {isPl ? "Zamów inny wariant" : "Order another variant"}
                </button>
                <button
                  type="button"
                  className="rounded-lg border border-[#00adad] bg-[linear-gradient(135deg,#00adad,#005a5a)] px-5 py-2 text-sm font-semibold text-white shadow-lg shadow-cyan-950/20 disabled:opacity-50"
                  onClick={saveAndReturnToShop}
                  disabled={saving || !canOrder || readOnly}
                >
                  {saving ? t("ordering") : isPl ? "Zamów" : "Order"}
                </button>
              </div>
            </div>
          ) : (
            <div className="overflow-auto px-5 py-4">
              <div className="space-y-4">
                <div className="rounded-lg border border-white/10 bg-white/5 p-4">
                  <div className="flex items-center justify-between gap-3 text-sm">
                    <span className="text-white/45">{isPl ? "Format" : "Format"}</span>
                    <span className="text-right font-medium">{productFormatLabel(product.format_key, locale)}</span>
                  </div>
                  <div className="mt-2 text-xs text-white/50">
                    {isPl ? "Format i orientacja pozostają bez zmian." : "Format and orientation stay unchanged."}
                  </div>
                </div>

                {coverChoices.length > 1 ? (
                  <div>
                    <div className="mb-2 text-xs font-semibold uppercase tracking-[0.08em] text-white/45">
                      {isPl ? "Okładka" : "Cover"}
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {coverChoices.map((choice) => (
                        <button
                          key={choice.key}
                          type="button"
                          className={`rounded-lg border px-3 py-2 text-left text-sm transition-colors ${
                            selectedBinding === choice.binding && selectedCoverFinish === choice.finish
                              ? "border-[#00adad] bg-[#00adad]/15 text-white"
                              : "border-white/10 bg-white/5 text-white/80 hover:bg-white/10"
                          }`}
                          onClick={() => selectVariantByOptions({ binding: choice.binding, coverFinish: choice.finish })}
                        >
                          {choice.label}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : null}

                {paperOptions.length > 1 ? (
                  <div>
                    <div className="mb-2 text-xs font-semibold uppercase tracking-[0.08em] text-white/45">
                      {isPl ? "Papier" : "Paper"}
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {paperOptions.map((finish) => (
                        <button
                          key={finish}
                          type="button"
                          className={`rounded-lg border px-3 py-2 text-left text-sm transition-colors ${
                            selectedPaperFinish === finish
                              ? "border-[#00adad] bg-[#00adad]/15 text-white"
                              : "border-white/10 bg-white/5 text-white/80 hover:bg-white/10"
                          }`}
                          onClick={() => selectVariantByOptions({ paperFinish: finish })}
                        >
                          {paperLabel(finish, locale)}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : null}

                <div className="rounded-lg border border-white/10 bg-white/5 p-4">
                  {variantLoading ? (
                    <div className="text-sm text-white/70">
                      {isPl ? "Ładowanie wariantów..." : "Loading variants..."}
                    </div>
                  ) : variantItems.length === 0 ? (
                    <div className="text-sm text-white/70">
                      {isPl ? "Brak kompatybilnych wariantów." : "No compatible variants."}
                    </div>
                  ) : (
                    <div className="grid gap-3 text-sm sm:grid-cols-2">
                      <div>
                        <div className="text-white/45">{isPl ? "Wybrany wariant" : "Selected variant"}</div>
                        <div className="mt-1 font-semibold">{productVariantLabel(selectedVariant, locale)}</div>
                      </div>
                      <div>
                        <div className="text-white/45">{isPl ? "Grzbiet po zmianie" : "Spine after change"}</div>
                        <div className="mt-1 font-semibold">{selectedSpineMm.toFixed(2)} mm</div>
                      </div>
                    </div>
                  )}
                </div>

                {spineChanged ? (
                  <div className="rounded-lg border border-amber-300/30 bg-amber-300/10 p-3 text-sm text-amber-100">
                    {isPl
                      ? `Grzbiet zostanie przeliczony z ${coverSpineMm.toFixed(2)} mm na ${selectedSpineMm.toFixed(2)} mm.`
                      : `The spine will be recalculated from ${coverSpineMm.toFixed(2)} mm to ${selectedSpineMm.toFixed(2)} mm.`}
                  </div>
                ) : null}

                {selectedCoverGeometryChanged ? (
                  <div className="rounded-lg border border-amber-300/30 bg-amber-300/10 p-3 text-sm text-amber-100">
                    <div className="font-semibold">
                      {isPl ? "Zmienia się geometria okładki." : "The cover geometry will change."}
                    </div>
                    <div className="mt-1 text-amber-100/85">
                      {isPl
                        ? "Po zmianie oprawy edytor przeliczy okładkę i przełączy widok na okładkę. Sprawdź ułożenie zdjęć, tekstu, grzbietu i zawijki przed zamówieniem."
                        : "After changing the binding, the editor will recalculate the cover and switch to the cover view. Review photos, text, spine and wrap before ordering."}
                    </div>
                    <label className="mt-3 flex items-start gap-2 text-sm text-amber-50">
                      <input
                        type="checkbox"
                        className="mt-0.5 h-4 w-4 shrink-0 accent-[#00adad]"
                        checked={coverChangeAccepted}
                        onChange={(e) => setCoverChangeAccepted(e.target.checked)}
                      />
                      <span>
                        {isPl
                          ? "Rozumiem i akceptuję, że okładka wymaga sprawdzenia po tej zmianie."
                          : "I understand and accept that the cover needs review after this change."}
                      </span>
                    </label>
                  </div>
                ) : null}

                {variantError ? (
                  <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-100">
                    {variantError}
                  </div>
                ) : null}
              </div>

              <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:justify-between">
                <button
                  type="button"
                  className="rounded-lg border border-white/10 bg-white/5 px-4 py-2 text-sm font-medium text-white hover:bg-white/10 disabled:opacity-50"
                  onClick={() => setOrderDialogMode("review")}
                  disabled={changingVariant}
                >
                  {isPl ? "Wróć" : "Back"}
                </button>
                <button
                  type="button"
                  className="rounded-lg border border-[#00adad] bg-[linear-gradient(135deg,#00adad,#005a5a)] px-5 py-2 text-sm font-semibold text-white shadow-lg shadow-cyan-950/20 disabled:opacity-50"
                  onClick={applySelectedVariant}
                  disabled={
                    changingVariant ||
                    variantLoading ||
                    !selectedVariantSku ||
                    selectedVariantSku === product.sku ||
                    (selectedCoverGeometryChanged && !coverChangeAccepted) ||
                    readOnly
                  }
                >
                  {changingVariant ? (isPl ? "Zmienianie..." : "Changing...") : isPl ? "Zastosuj wariant" : "Apply variant"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  function renderGuestNamePrompt() {
    if (!guestNamePromptOpen) return null;

    return (
      <div className="fixed inset-0 z-[9999]">
        <div className="absolute inset-0 bg-black/40" />
        <div className="fixed left-1/2 top-1/2 w-[420px] max-w-[92vw] -translate-x-1/2 -translate-y-1/2 rounded-xl border border-white/10 bg-[#0f141d] p-4 shadow-2xl">
          <div className="text-sm font-semibold text-white">Twoje imię</div>
          <div className="mt-1 text-xs text-white/70">
            Będzie widoczne dla innych podczas pracy nad projektem.
          </div>
          <input
            className="mt-3 w-full rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm text-white outline-none"
            value={guestNameDraft}
            onChange={(e) => setGuestNameDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") submitGuestName();
            }}
            placeholder="np. Anna"
            autoFocus
          />
          <div className="mt-4 flex gap-2">
            <button
              type="button"
              className="flex-1 rounded-lg bg-white px-4 py-2 text-sm font-semibold text-black hover:bg-white/90"
              onClick={submitGuestName}
            >
              Dalej
            </button>
            <button
              type="button"
              className="flex-1 rounded-lg border border-white/10 bg-white/5 px-4 py-2 text-sm text-white hover:bg-white/10"
              onClick={skipGuestName}
            >
              Pomiń
            </button>
          </div>
        </div>
      </div>
    );
  }

	  if (accessRequired) {
	    const openUrl = buildOpenEditorShopUrl(accessRequired.sku, accessRequired.projectPublicId);
	    const shopBase = getWpBaseUrl();
	    return (
      <AccessGate
        title={locale === "pl" ? "Sprawdzamy dostęp do projektu" : "Checking project access"}
        message={
          locale === "pl"
            ? "Ten projekt jest chroniony i można go otworzyć tylko przez bezpieczny link powiązany z Twoim kontem."
            : "This project is protected and can only be opened through a secure link connected to your account."
        }
        redirectMessage={
          locale === "pl"
            ? "Jeśli masz dostęp do projektu, za chwilę otworzymy go automatycznie. Jeśli nie, poprosimy Cię o zalogowanie w sklepie."
            : "If you have access to this project, it will open automatically. Otherwise, you will be asked to sign in to the shop."
        }
        primaryHref={openUrl}
        primaryLabel={locale === "pl" ? "Kontynuuj" : "Continue"}
        secondaryHref={shopBase}
        secondaryLabel={locale === "pl" ? "Wróć do sklepu" : "Back to shop"}
        footer={
          locale === "pl"
            ? "Jeśli przekierowanie nie zadziała, kliknij „Kontynuuj”."
            : 'If the redirect does not work, click "Continue".'
        }
        loaderText={locale === "pl" ? "Łączenie z bezpiecznym linkiem..." : "Connecting to secure link..."}
        autoRedirectHref={openUrl}
        autoRedirectDelayMs={8000}
      />
	    );
	  }

  const projectImagePreloadPercent =
    projectImagePreload.total > 0
      ? Math.round(clamp(projectImagePreload.done / projectImagePreload.total, 0, 1) * 100)
      : 0;
  const projectImagePreloadOverlay = projectImagePreload.running ? (
    <div className="fixed inset-0 z-[20000] flex items-center justify-center bg-black/88 p-4 text-white">
      <div className="flex w-full max-w-[320px] flex-col items-center gap-4 text-center">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-white/25 border-t-white" />
        <div className="text-sm font-semibold">
          {locale === "pl" ? "Trwa ładowanie projektu" : "Loading project"}
        </div>
        <div className="space-y-1">
          <div className="text-xs font-medium text-white/70">{projectImagePreloadPercent}%</div>
          {projectImagePreload.label ? (
            <div className="text-[11px] font-medium text-white/55">{projectImagePreload.label}</div>
          ) : null}
        </div>
        <div className="h-2 w-full overflow-hidden rounded-full bg-white/15">
          <div
            className="h-full rounded-full bg-white transition-[width] duration-200"
            style={{ width: `${projectImagePreloadPercent}%` }}
          />
        </div>
      </div>
    </div>
  ) : null;

  if (error) {
    return (
      <main className="p-6">
        <h1 className="text-xl font-semibold">{t("errorTitle")}</h1>
        <pre className="mt-3 rounded-lg bg-red-50 p-3 text-sm text-red-700 overflow-auto">{error}</pre>
        {renderGuestNamePrompt()}
      </main>
    );
  }

  if (!config || !product || !project || !store.contentData || !store.coverData) {
    return (
      <main className="min-h-dvh p-6">
        <div className="text-sm text-gray-600">{t("loading")}</div>
        {renderGuestNamePrompt()}
        {projectImagePreloadOverlay}
      </main>
    );
  }

  return (
    <div className="editor-shell flex h-dvh flex-col">
      <header className="editor-topbar flex h-[60px] shrink-0 items-center justify-between px-3 text-white md:h-[72px] md:px-6">
        <div className="flex min-w-0 items-center gap-2 md:gap-6">
          <div className="min-w-0 shrink">
            <div className="text-lg font-bold leading-none text-white md:text-[22px]">PrintBook</div>
            <div title={productTitle} className="mt-0.5 max-w-[132px] truncate text-xs font-medium text-[#a2aec6] sm:max-w-[220px] md:mt-1 md:max-w-[300px] md:text-sm">{project.name && !/^(editor|theme):/.test(project.name) ? project.name : productTitle}</div>
          </div>

          <div className="hidden md:block">
            <LocaleSwitcher />
          </div>

          <button
            className="editor-secondary-button px-3 py-1 text-xs disabled:opacity-50 md:px-4 md:text-sm"
            data-onboarding-target="save-button"
            onClick={async () => {
              try {
                setSaving(true);
                await saveDraftsNow();
                showToast(t("saved"), "ok");
              } catch {
                showToast(t("saveFailed"), "error");
              } finally {
                setSaving(false);
              }
            }}
            disabled={saving || readOnly}
            type="button"
            title={
              readOnly
                ? lock?.locked && !lock?.mine
                  ? locale === "pl" ? `Zablokowane przez ${lock.by || "inną osobę"}` : `Locked by ${lock.by || "another user"}`
                  : locale === "pl" ? "Tylko do odczytu" : "Read only"
                : undefined
            }
          >
            {t("save")}
          </button>
          {canShare ? (
            <button type="button" aria-label={locale === "pl" ? "Zapisz jako" : "Save as"}
              title={locale === "pl" ? "Zapisz jako nowy projekt" : "Save as a new project"}
              className="editor-secondary-button shrink-0 px-3 py-1 text-xs disabled:opacity-50 md:px-4 md:text-sm"
              disabled={saving || copyingProject || uploading || bulkProgress.running || changingVariant || readOnly}
              onClick={() => { saveAsPausedRef.current = true; setCopyError(null); setSaveAsOpen(true); }}>
              <span className="hidden sm:inline">{locale === "pl" ? "Zapisz jako" : "Save as"}</span>
              <svg className="sm:hidden" aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <rect x="8" y="8" width="12" height="12" rx="2" /><path d="M16 8V4H4v12h4" />
              </svg>
            </button>
          ) : null}
        </div>

        <div className="flex shrink-0 items-center gap-2 md:gap-3">
          <button
            type="button"
            className="editor-secondary-button px-3 py-1 text-xs disabled:opacity-50 md:px-4 md:text-sm"
            data-onboarding-target="help-button"
            onClick={() => {
              setShowShare(false);
              setShowHelp(true);
            }}
            title={locale === "pl" ? "Pomoc" : "Help"}
          >
            <span className="hidden sm:inline">{locale === "pl" ? "Pomoc" : "Help"}</span>
            <span className="sm:hidden">?</span>
          </button>

          <button
            type="button"
            className="editor-secondary-button px-3 py-1 text-xs disabled:opacity-50 md:px-4 md:text-sm"
            data-onboarding-target="share-button"
            onClick={() => {
              setShowHelp(false);
              setShowShare(true);
            }}
            disabled={!canShare}
            title={locale === "pl" ? "Udostępnij" : "Share"}
          >
            <span className="hidden sm:inline">{locale === "pl" ? "Udostępnij" : "Share"}</span>
            <span className="sm:hidden" aria-hidden="true">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <circle cx="18" cy="5.5" r="3" stroke="currentColor" strokeWidth="1.9" />
                <circle cx="6" cy="12" r="3" stroke="currentColor" strokeWidth="1.9" />
                <circle cx="18" cy="18.5" r="3" stroke="currentColor" strokeWidth="1.9" />
                <path d="m8.65 10.65 6.7-3.65M8.65 13.35l6.7 3.65" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
              </svg>
            </span>
          </button>

          <button
            className="editor-gradient-button px-3 py-2 text-sm disabled:opacity-50 md:px-4 md:text-base"
            data-onboarding-target="order-button"
            style={{
              background: "linear-gradient(135deg, #00adad, #005a5a)",
              borderColor: "#00adad"
            }}
            onClick={openOrderReview}
            disabled={saving || !canOrder || readOnly}
            type="button"
            title={
              !canOrder
                ? locale === "pl" ? "Gość nie może złożyć zamówienia" : "Guests cannot place orders"
                : readOnly
                  ? lock?.locked && !lock?.mine
                    ? locale === "pl" ? `Zablokowane przez ${lock.by || "inną osobę"}` : `Locked by ${lock.by || "another user"}`
                    : locale === "pl" ? "Tylko do odczytu" : "Read only"
                  : undefined
            }
          >
            {saving ? t("ordering") : t("order")}
          </button>
        </div>
      </header>

      <div className="flex min-h-0 flex-1 overflow-hidden">
        <aside
          className={`editor-sidebar hidden shrink-0 overflow-y-auto border-r transition-[width] duration-200 md:block ${
            toolsCollapsed ? "w-[104px]" : "w-[232px]"
          }`}
        >
          <div className={toolsCollapsed ? "p-2" : "p-4"}>
            <RightToolsPanel
              onAddTextRole={addTextWithRole}
              onAddShape={addShape}
              onAddRect={addRect}
              onAddBackground={addBackground}
              backgroundColor={bgColor}
              onApplySolidBackgroundColor={applySolidBackgroundColor}
              onApplyBackgroundPattern={applyBackgroundPattern}
              showImages={showImages}
              onOpenImages={openImagesPopup}
              onCloseImages={() => setShowImages(false)}
              onInsertLibraryAsset={insertAsset}
              onInsertClipart={insertClipart}
              onUploadImage={onPickImage}
              onBulkUpload={onBulkUpload}
              imageLibraryRefreshNonce={imageLibraryRefreshNonce}
              themeTemplates={visibleThemeTemplates}
              onApplyTheme={applyThemeById}
              onInsertSpreadTemplate={insertSpreadTemplateById}
              canCreateThemeTemplate={sessionRole === "admin"}
              onCreateThemeTemplate={createThemeTemplateFromCurrentProject}
              themeTemplateCreateBusy={themeTemplateCreateBusy}
              onDeleteThemeTemplate={deleteGeneratedThemeTemplate}
              themeTemplateDeleteBusyId={themeTemplateDeleteBusyId}
              activeFormatGroup={activeFormatGroup}
              uploading={uploading}
              bulkProgress={bulkProgress}
              canUndoTheme={canUndoTheme}
              onUndoTheme={undoLastTheme}
              readOnly={readOnly}
              helpAction={helpAction}
              helpActionNonce={helpActionNonce}
              presenceLabel={presenceLabel()}
              collapsed={toolsCollapsed}
              onCollapsedChange={setToolsCollapsed}
            />
          </div>
        </aside>

        <main
          className={`editor-shell flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden pb-[calc(104px+env(safe-area-inset-bottom))] pt-4 md:pb-8 md:pt-7 ${
            isActiveContentSpread ? "px-2 md:px-4" : "px-3 md:px-8"
          }`}
        >
          <div className="mb-2 flex items-center justify-center gap-2 md:mb-5 md:gap-4">
            <button
              type="button"
              className="editor-zoom-button"
              onClick={() => {
                setZoomTouched(true);
                setManualZoom((value) => clamp(value - 0.1, 0.25, 3));
              }}
              title={t("view.zoomOut")}
              aria-label={t("view.zoomOut")}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M5 12h14" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
              </svg>
            </button>

            <span className="editor-zoom-readout">
              {Math.round(manualZoom * 100)}%
            </span>

            <button
              type="button"
              className="editor-zoom-button"
              onClick={() => {
                setZoomTouched(true);
                setManualZoom((value) => clamp(value + 0.1, 0.25, 3));
              }}
              title={t("view.zoomIn")}
              aria-label={t("view.zoomIn")}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
              </svg>
            </button>

            {zoomTouched ? (
              <div className="ml-2 flex items-center gap-2">
                <IconLabelBtn
                  icon={I.center}
                  label={t("view.centerShort")}
                  title={t("view.center")}
                  onClick={centerOnSelection}
                  disabled={!stageMetrics}
                />

                <IconLabelBtn
                  icon={I.reset}
                  label={t("view.resetShort")}
                  title={t("view.reset")}
                  onClick={() => {
                    setZoomTouched(false);
                    setManualZoom(fitZoom);
                    requestAnimationFrame(() => requestAnimationFrame(() => centerScrollToCanvas(fitZoom)));
                  }}
                />
              </div>
            ) : null}
          </div>

          <div className="flex-1 min-h-0 overflow-hidden">
            <div className="relative h-full">
              <div
                ref={canvasViewportRef}
                data-onboarding-target="canvas"
                style={{ touchAction: "none" }}
                className={`h-full w-full ${zoomTouched ? "overflow-auto" : "overflow-hidden"}`}
                onWheel={(e) => {
                  if (!(e.ctrlKey || e.metaKey)) return;
                  e.preventDefault();
                  const step = e.deltaY > 0 ? -0.08 : 0.08;
                  setZoomTouched(true);
                  setManualZoom((z) => clamp(z + step, 0.25, 3));
                }}
              >
                <div
                  className="flex min-h-full items-center justify-center"
                  style={{ width: canvasWrapperWidth, paddingBottom: canvasBottomReserve }}
                >
                  {fontsReady ? (
                    store.activeDoc === "content" ? (
                      <ContentBookSpreadsCanvas
                        formats={config.formats}
                        pageWidthMm={contentPageWidthMm}
                        fitViewport={{
                          w: canvasViewport.w,
                          h: Math.max(100, canvasViewport.h - canvasBottomReserve)
                        }}
                        zoomMode={zoomTouched ? "manual" : "fit"}
                        manualZoom={manualZoom}
                        onCopySelected={onCopySelected}
                        onPaste={onPaste}
                        onDuplicate={onDuplicate}
                        onElementSelect={openPropertiesForSelectedElement}
                        onImagePlaceholderDoubleClick={openImagesForPlaceholder}
                        onStageMetrics={setStageMetrics}
                        onSelectionCenter={setSelectionCenter}
                        onThumbnail={handleThumb}
                        onDropAsset={readOnly ? undefined : insertAssetAt}
                        readOnly={readOnly}
                      />
                    ) : (
                      <CanvasEditor
                        doc="cover"
                        formats={config.formats}
                        pageIndex={store.coverPageIndex}
                        fitViewport={{
                          w: canvasViewport.w,
                          h: Math.max(100, canvasViewport.h - canvasBottomReserve)
                        }}
                        zoomMode={zoomTouched ? "manual" : "fit"}
                        manualZoom={manualZoom}
                        onCopySelected={onCopySelected}
                        onPaste={onPaste}
                        onDuplicate={onDuplicate}
                        onElementSelect={openPropertiesForSelectedElement}
                        onImagePlaceholderDoubleClick={openImagesForPlaceholder}
                        onStageMetrics={setStageMetrics}
                        onSelectionCenter={setSelectionCenter}
                        onDropAsset={readOnly ? undefined : insertAssetAt}
                        readOnly={readOnly}
                      />
                    )
                  ) : (
                    <div className="text-sm text-gray-600">{t("loadingFonts")}</div>
                  )}
                </div>
              </div>

              {store.activeDoc === "content" ? (
                <div ref={paginationRef} className="pointer-events-none absolute bottom-0 left-0 right-0 flex justify-center">
                  <div className="pointer-events-auto">
                    <UnitPagination />
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </main>

        <aside className="editor-sidebar hidden w-[260px] shrink-0 overflow-hidden border-l lg:block">
          <RightPagesPanel
            maxPages={product?.max_pages ?? 190}
            thumbs={thumbs}
            formats={config.formats}
            coverPropertiesPanel={spineTitlePanel}
          />
        </aside>
      </div>

      {thumbPreloadQueue.length > 0 && fontsReady && store.contentData ? (
        <div className="pointer-events-none absolute -left-[99999px] top-0 h-0 w-0 overflow-hidden opacity-0">
          <ContentBookSpreadsCanvas
            formats={config.formats}
            pageWidthMm={contentPageWidthMm}
            fitViewport={{ w: 320, h: 220 }}
            zoomMode="fit"
            manualZoom={1}
            forcedUnitIndex={thumbPreloadQueue[0]}
            thumbnailOnly
            onThumbnailReady={handlePreloadedThumb}
          />
        </div>
      ) : null}

      <MobileEditorToolbar
        locale={locale}
        readOnly={readOnly}
        onAddTextRole={addTextWithRole}
        onOpenImages={() => openMobileTool({ type: "open_tool", tool: "image" })}
        onOpenClipart={() => openMobileTool({ type: "open_tool", tool: "clipart" })}
        onOpenShapes={() => openMobileTool({ type: "open_tool", tool: "shapes" })}
        onOpenTemplates={() => openMobileTool({ type: "open_templates", tab: "themes" })}
        onAddBackground={() => openMobileTool({ type: "open_tool", tool: "background" })}
        onShowPages={() => setMobilePagesOpen(true)}
      />

      <div className="md:hidden">
        <RightToolsPanel
          onAddTextRole={addTextWithRole}
          onAddShape={addShape}
          onAddRect={addRect}
          onAddBackground={addBackground}
          backgroundColor={bgColor}
          onApplySolidBackgroundColor={applySolidBackgroundColor}
          onApplyBackgroundPattern={applyBackgroundPattern}
          showImages={showImages}
          onOpenImages={openImagesPopup}
          onCloseImages={() => setShowImages(false)}
          onInsertLibraryAsset={insertAsset}
          onInsertClipart={insertClipart}
          onUploadImage={onPickImage}
          onBulkUpload={onBulkUpload}
          imageLibraryRefreshNonce={imageLibraryRefreshNonce}
          themeTemplates={visibleThemeTemplates}
          onApplyTheme={applyThemeById}
          onInsertSpreadTemplate={insertSpreadTemplateById}
          canCreateThemeTemplate={sessionRole === "admin"}
          onCreateThemeTemplate={createThemeTemplateFromCurrentProject}
          themeTemplateCreateBusy={themeTemplateCreateBusy}
          onDeleteThemeTemplate={deleteGeneratedThemeTemplate}
          themeTemplateDeleteBusyId={themeTemplateDeleteBusyId}
          activeFormatGroup={activeFormatGroup}
          uploading={uploading}
          bulkProgress={bulkProgress}
          canUndoTheme={canUndoTheme}
          onUndoTheme={undoLastTheme}
          readOnly={readOnly}
          helpAction={mobileToolAction}
          helpActionNonce={mobileToolActionNonce}
          launcherOnly
        />
      </div>

      <MobileBottomSheet
        isOpen={mobilePagesOpen}
        title={locale === "pl" ? "Strony" : "Pages"}
        onClose={() => setMobilePagesOpen(false)}
      >
        <div className="h-[72dvh]">
          <RightPagesPanel
            maxPages={product?.max_pages ?? 190}
            thumbs={thumbs}
            formats={config.formats}
            coverPropertiesPanel={spineTitlePanel}
          />
        </div>
      </MobileBottomSheet>

      <HelpChatModal
        isOpen={showHelp}
        onClose={() => setShowHelp(false)}
        onAction={handleHelpAction}
        onStartGuide={openOnboardingGuide}
      />

      <EditorOnboardingGuide
        isOpen={showOnboardingGuide}
        locale={locale}
        canShare={canShare}
        onClose={closeOnboardingGuide}
        onAction={handleOnboardingAction}
      />

      {store.projectId && product ? (
        <ShareInvitesModal
          isOpen={showShare}
          onClose={() => setShowShare(false)}
          projectId={store.projectId}
          projectPublicId={project.public_id}
          sku={product.sku}
          locale={locale}
          apiGet={apiGet}
          apiPost={apiPost}
          apiDelete={apiDelete}
          canShare={canShare}
        />
      ) : null}

      {renderOrderDialog()}
      {saveAsOpen ? <SaveAsDialog locale={locale}
        initialName={`${project.name && !/^(editor|theme):/.test(project.name) ? project.name : productTitle} - ${locale === "pl" ? "kopia" : "copy"}`}
        busy={copyingProject} error={copyError} onSubmit={saveProjectAs}
        onClose={() => { if (!copyingProject) { saveAsPausedRef.current = false; setSaveAsOpen(false); } }} /> : null}

      {renderGuestNamePrompt()}

      {toast ? (
        <div
          className={`fixed bottom-6 right-6 rounded-xl border px-4 py-3 text-sm shadow-lg ${
            toast.kind === "ok"
              ? "bg-emerald-50 text-emerald-800 border-emerald-200"
              : "bg-red-50 text-red-800 border-red-200"
          }`}
        >
          {toast.message}
        </div>
      ) : null}

      {projectImagePreloadOverlay}

      <PropertiesDrawer
        isOpen={propsDrawerOpen}
        onClose={() => {
          setPropsDrawerOpen(false);
          setToolsCollapsed(false);
        }}
        desktopLeft={toolsCollapsed ? 104 : 0}
        fonts={filteredFonts}
        frames={filteredFrames}
        formats={config.formats}
        textPresets={projectSettings?.text_presets || {}}
        onSaveTextPreset={saveTextPreset}
      />
    </div>
  );
}
